import React, { useCallBack, useState } from "react";
import axios from "axios";
import { useAuthHeader } from "react-auth-kit";
import { authName } from "../App";
import { redirect } from "react-router-dom";
import { showNotification } from "@mantine/notifications";
import { IconExclamationMark } from "@tabler/icons";
import _ from "lodash";
import { requestFileDownload } from "../utils/fileDownload";
import {
  downloadMigrationTemplate,
  getMigrationBatchRows,
  listMigrationBatches,
  postDataMigrationForm,
  stopMigrationBatch,
  undoMigrationBatch,
} from "../helpers/dataMigrationApi";
import { downloadMigrationErrorCsv } from "../helpers/migrationErrorDownload";
import { downloadCronExecutionResultCsv } from "../helpers/cronExecutionLogDownload";
import {
  buildCompanyHeaders,
  getStoredCompanyId,
} from "../helpers/companyWorkspace";
import { activityLogSearchQuery } from "../helpers/activityLogVisibility";

function getAuthToken() {
  return localStorage.getItem(authName) || "";
}

function clearAuthSession() {
  localStorage.removeItem(authName);
  localStorage.removeItem(`${authName}_storage`);
  localStorage.removeItem(`${authName}_type`);
  localStorage.removeItem(`${authName}_state`);
}

export const useServerApi = () => {
  const getHeader = (method = "GET", body) => {
    const token = getAuthToken();
    const jsonHeaders = buildCompanyHeaders(
      {
        Accept: "application/json",
        "Content-Type": "application/json",
        "x-access-token": token,
        "Access-Control-Allow-Origin": "*",
      },
      getStoredCompanyId()
    );
    switch (method) {
      case "GET":
        return {
          method: "GET",
          headers: jsonHeaders,
        };

      case "POST": {
        return {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify(body),
        };
      }

      case "DELETE": {
        return {
          method: "DELETE",
          headers: jsonHeaders,
        };
      }

      case "PUT": {
        return {
          method: "PUT",
          headers: jsonHeaders,
          body: JSON.stringify(body),
        };
      }
    }
  };

  const handleResponseError = (result) => {
    if (result.success) return;
    // console.log("handleResponseError", result.message);
    if (result.message && result.message === "ERR_INVALID_TOKEN") {
      if (window.location.pathname === "/login") return;
      clearAuthSession();
      // Must use a browser-level redirect: useNavigate() inside tab panels
      // targets the per-tab memory router, not the app shell route.
      window.location.assign("/login");
    }
  };

  const aggregate = async ({ apiEntity, aggregations }) => {
    let url = `${process.env.REACT_APP_SERVER_URL}/${apiEntity}/aggregate`;

    try {
      let result = await fetch(url, getHeader("POST", { aggregations }));
      result = await result.json();
      if (!result || !result.success) return handleResponseError(result);

      return result.data;
    } catch (error) {
      // console.log(error);
      return null;
    }
  };

  const search = async ({
    apiEntity,
    pageSize = 50,
    currentPage = 1,
    sort,
    searchQuery,
    searchText,
    searchByTextField,
    preQuery,
    select,
    byAggregation = true,
  }) => {
    try {
      let url = `${
        process.env.REACT_APP_SERVER_URL
      }/${apiEntity}/search?limit=${pageSize}&page=${currentPage - 1}`;

      if (sort) {
        url += sort.by ? `&sort=${sort.by}` : "";
        url += sort.by ? `&order=${sort.order}` : "";
      }

      if (select) {
        url += `&select=${select}`;
      }

      let result = await fetch(
        url,
        getHeader("POST", {
          condition: searchQuery,
          byAggregation,
          searchByTextField,
          searchText,
          preQuery,
        })
      );

      result = await result.json();
      // console.log("search", result);
      if (!result || !result.success) return handleResponseError(result);
      return result.data;
    } catch (error) {
      // console.log(error);
      return null;
    }
  };

  const getExtraFieldSettingByEntityType = async (entityType) => {
    try {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/extraFieldSetting/getByEntityType/${entityType}`,
        getHeader("GET")
      );
      const json = await result.json();
      if (!json?.success) return handleResponseError(json);
      return json.data;
    } catch (error) {
      return null;
    }
  };

  const upsertExtraFieldSettingByEntityType = async (values) => {
    try {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/extraFieldSetting/upsertByEntityType`,
        getHeader("POST", values)
      );
      const json = await result.json();
      if (!json?.success) {
        handleResponseError(json);
        const message =
          json?.error?.message ||
          json?.error ||
          json?.message ||
          "Failed to save display options";
        throw new Error(
          typeof message === "string" ? message : "Failed to save display options"
        );
      }
      return json.data;
    } catch (error) {
      throw error;
    }
  };

  const previewPointType = async ({ pointType, order }) => {
    try {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/pointType/preview`,
        getHeader("POST", { pointType, order })
      );
      const json = await result.json();
      if (!json?.success) {
        handleResponseError(json);
        const message =
          json?.error?.message ||
          json?.error ||
          json?.message ||
          "Failed to preview points";
        throw new Error(
          typeof message === "string" ? message : "Failed to preview points"
        );
      }
      return json.data;
    } catch (error) {
      throw error;
    }
  };

  const previewPromotionTrack = async ({ track, sample }) => {
    try {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/promotionTrack/preview`,
        getHeader("POST", { track, sample })
      );
      const json = await result.json();
      if (!json?.success) {
        handleResponseError(json);
        const message =
          json?.error?.message ||
          json?.error ||
          json?.message ||
          "Failed to preview promotion";
        throw new Error(
          typeof message === "string" ? message : "Failed to preview promotion"
        );
      }
      return json.data;
    } catch (error) {
      throw error;
    }
  };

  const getPromotionProgress = async (userId) => {
    try {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/user/promotionProgress/${userId}`,
        getHeader("GET")
      );
      const json = await result.json();
      if (!json?.success) {
        handleResponseError(json);
        const message =
          json?.error?.message ||
          json?.error ||
          json?.message ||
          "Failed to load promotion progress";
        throw new Error(
          typeof message === "string"
            ? message
            : "Failed to load promotion progress"
        );
      }
      return json.data;
    } catch (error) {
      throw error;
    }
  };

  const add = async ({ apiEntity, values }) => {
    try {
      let result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/${apiEntity}`,
        getHeader("POST", values)
      );
      result = await result.json();

      return result;
    } catch (error) {
      // console.log(error);
    }
  };

  const removeById = async ({ apiEntity, id }) => {
    try {
      let result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/${apiEntity}/${id}`,
        getHeader("DELETE")
      );
      result = await result.json();
      if (!result.success && result.error) throw result.error;
      return result;
    } catch (error) {
      // console.log(error);
      showNotification({
        title: `Delete fail`,
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: _.isString(error) ? error : error?.message ?? error?.msg,
      });
    }
  };

  const getDistinctValues = async ({ apiEntity, field, populate }) => {
    try {
      let result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/${apiEntity}/getDistinct`,
        getHeader("POST", {
          field,
          populate,
        })
      );
      result = await result.json();
      // console.log("getDistinctValues", result);

      if (!result.success) return handleResponseError(result);
      return result.data;
    } catch (error) {
      // console.log(error);
    }
  };

  const getById = async ({ apiEntity, id }) => {
    try {
      if (!id || !apiEntity) return null;
      let result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/${apiEntity}/${id}`,
        getHeader("GET")
      );
      result = await result.json();
      if (!result || !result.success) {
        handleResponseError(result);
        const err = new Error(result?.message ?? "ERR_GET_DATA");
        err.code = result?.message;
        if (result?.error != null) {
          err.detail =
            typeof result.error === "string"
              ? result.error
              : result.error?.message ?? "";
        }
        throw err;
      }

      return result.data;
    } catch (error) {
      if (error?.code) throw error;
      // console.log(error);
    }
  };

  const copyById = async ({
    apiEntity,
    id,
    newCode = false,
    fields,
    uniqueFields,
  }) => {
    try {
      // console.log("copyById", id, apiEntity, fields, uniqueFields);
      if (!id || !apiEntity) return null;
      let result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/${apiEntity}/${id}`,
        getHeader("GET")
      );
      result = await result.json();
      if (!result || !result.success) return handleResponseError(result);

      let newData = {};

      //only copy the fields if fields is provided
      // console.log("copyById", fields);
      if (fields) {
        fields.forEach((field) => {
          newData[field] = result.data[field];
        });
        //check field is unique by mongoose unique
      } else {
        newData = { ...result.data };
        delete newData._id;
        delete newData.createdAt;
        if (newCode) delete newData.code;
        if (newData.code) {
          newData.code += "(copy)";
        }
      }

      // Append Copy to the unique fields
      if (uniqueFields) {
        uniqueFields.forEach((field) => {
          newData[field] = result.data[field];
          if (newData[field]) {
            newData[field] += "(copy)";
          }
        });
      }

      // console.log("copyById 2 > newData", newData);
      let result2 = await add({ apiEntity, values: newData });
      if (!result2 || !result2.success) return handleResponseError(result2);

      let data = await getById({ apiEntity, id: result2.data._id });
      return data;
    } catch (error) {
      // console.log(error);
    }
  };

  const updateById = async ({ apiEntity, values, id }) => {
    try {
      let result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/${apiEntity}/${id}`,
        getHeader("PUT", values)
      );
      result = await result.json();
      // console.log("Update", apiEntity, result, values);
      return result;
    } catch (error) {
      // console.log(error);
    }
  };

  const importBatch = async ({ apiEntity, file, importData }) => {
    try {
      let data = new FormData();
      data.append("file", file);
      data.append("filename", file.name); // use orginial name
      data.append("token", getAuthToken());
      data.append("importData", JSON.stringify(importData));

      let result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/${apiEntity}/importBatch`,
        {
          method: "POST",
          body: data,
          redirect: "follow",
        }
      );
      result = await result.json();
      if (!result.success) {
        throw result.error ?? { message: result.message ?? "Import failed" };
      }
      return result;
    } catch (error) {
      // showNotification({
      //   title: `Import fail`,
      //   color: "red",
      //   icon: <IconExclamationMark size={18} />,
      //   message: _.isString(error) ? error : error?.message ?? error?.msg,
      // });
      console.log(error);
      throw error;
    }
  };

  const getErrorMsg = (error) => {
    try {
      if (error == null) return "";
      if (typeof error !== "object") return String(error);
      if (error.msg) return String(error.msg);
      let msg = Object.entries(error)
        .map(([key, value]) => value?.message ?? String(value))
        .join(" ,");
      // console.log(msg);
      return msg;
    } catch (error) {
      console.log(error);
    }
  };

  const uploadFile = async (
    uploadFolder,
    file,
    entityId,
    exisitingFiles,
    uploadOptions = {}
  ) => {
    try {
      let data = new FormData();

      data.append("file", file);
      data.append("filename", file.name);
      data.append("exisitingFiles", JSON.stringify(exisitingFiles));
      data.append("entityId", entityId);
      data.append("uploadFolder", uploadFolder);
      if (uploadOptions.entityType) {
        data.append("entityType", uploadOptions.entityType);
      }
      if (uploadOptions.entityField) {
        data.append("entityField", uploadOptions.entityField);
      }
      if (uploadOptions.entityCode) {
        data.append("entityCode", uploadOptions.entityCode);
      }
      data.append("token", getAuthToken());
      // console.log(
      //     "uploadFile",
      //     `${process.env.REACT_APP_SERVER_URL}/file/upload/${uploadFolder}`
      // );
      let result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/file/upload`,
        {
          method: "POST",
          body: data,
          redirect: "follow",
        }
      );

      result = await result.json();
      if (!result || !result.success) return handleResponseError(result);
      // console.log("uploadFile success", result);
      return result;
    } catch (error) {
      console.log(error);
    }
  };

  const renameFile = async (id, link, name) => {
    try {
      let result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/file/rename`,
        getHeader("POST", { id, link, name })
      );
      result = await result.json();
      return result;
    } catch (error) {
      console.log(error);
    }
  };

  const downloadZipFiles = async (filename, links) => {
    try {
      let result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/file/zip`,
        getHeader("POST", { filename, files: links })
      );

      result = await result.json();
      return result;
    } catch (error) {
      console.log(error);
    }
  };

  const downloadFiles = async (files, filename = "files.zip") => {
    const descriptors = (files || []).map((file) => ({
      link: file?.link,
      path: file?.path,
      url: file?.url,
      name: file?.name,
      filename: file?.filename,
      mimetype: file?.mimetype || file?.mimeType,
    }));

    return requestFileDownload({
      files: descriptors,
      filename,
      serverUrl: process.env.REACT_APP_SERVER_URL,
      token: getAuthToken(),
    });
  };

  const getOverridingResult = async ({
    preimum,
    rate,
    plan,
    providerExchangeRate,
    internalExchangeRate,
    skipProviderRate = false,
  }) => {
    try {
      let result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/commissionOverridingPlan/test`,
        getHeader("POST", {
          preimum,
          rate,
          plan,
          providerExchangeRate,
          internalExchangeRate,
          skipProviderRate,
        })
      );
      result = await result.json();
      return result;
    } catch (error) {
      console.log(error);
    }
  };
  const DataFeed = {
    import: async (file) => {
      try {
        let data = new FormData();
        data.append("file", file);
        data.append("filename", file.name); // use orginial name
        data.append("token", getAuthToken());
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/dataFeed/import`,
          {
            method: "POST",
            body: data,
            redirect: "follow",
          }
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    downloadIssues: async (id) => {
      let result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/dataFeed/downloadIssues/${id}`,
        getHeader("GET")
      );
      result = await result.json();
      return result;
    },
  };

  const DataMigration = {
    getSources: async () => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/dataMigration/sources`,
        getHeader("GET")
      );
      return result.json();
    },
    preview: async (file, step, options = {}) => {
      return postDataMigrationForm({
        serverUrl: process.env.REACT_APP_SERVER_URL,
        endpoint: "preview",
        file,
        step,
        options,
        token: getAuthToken(),
      });
    },
    generateTemplate: async (file, step, options = {}) => {
      const blob = await downloadMigrationTemplate({
        serverUrl: process.env.REACT_APP_SERVER_URL,
        file,
        step,
        options,
        token: getAuthToken(),
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `inspal-${step}-template.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      return { success: true };
    },
    importStep: async (file, step, options = {}) => {
      return postDataMigrationForm({
        serverUrl: process.env.REACT_APP_SERVER_URL,
        endpoint: "import",
        file,
        step,
        options,
        token: getAuthToken(),
      });
    },
    getBatch: async (id) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/dataMigration/batch/${id}`,
        getHeader("GET")
      );
      return result.json();
    },
    listBatches: async (page = 1, pageSize = 20, filters = {}) => {
      return listMigrationBatches({
        serverUrl: process.env.REACT_APP_SERVER_URL,
        token: getAuthToken(),
        page,
        pageSize,
        step: filters.step,
        status: filters.status,
      });
    },
    getBatchRows: async (batchId, page = 1, pageSize = 25, errorsOnly = false) => {
      return getMigrationBatchRows({
        serverUrl: process.env.REACT_APP_SERVER_URL,
        batchId,
        token: getAuthToken(),
        page,
        pageSize,
        errorsOnly,
      });
    },
    stopBatch: async (batchId) => {
      return stopMigrationBatch({
        serverUrl: process.env.REACT_APP_SERVER_URL,
        batchId,
        token: getAuthToken(),
      });
    },
    undoBatch: async (batchId) => {
      return undoMigrationBatch({
        serverUrl: process.env.REACT_APP_SERVER_URL,
        batchId,
        token: getAuthToken(),
      });
    },
    downloadErrors: async (batchId) => {
      await downloadMigrationErrorCsv({
        serverUrl: process.env.REACT_APP_SERVER_URL,
        batchId,
        token: getAuthToken(),
      });
      return { success: true };
    },
  };

  const OrderProfolio = {
    import: async (file) => {
      try {
        let data = new FormData();
        data.append("file", file);
        data.append("filename", file.name); // use orginial name
        data.append("token", getAuthToken());
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/profolioImport/import`,
          {
            method: "POST",
            body: data,
            redirect: "follow",
          }
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
    getValuations: async (policyNumber) => {
      try {
        let result = await search({
          apiEntity: "policyProfolio",
          pageSize: 100,
          searchQuery: {
            policyNumber,
          },
          select: "valuationDate _id policyNumbner",
        });
        // console.log("getValuations", result);
        result = await result.json();
        return result.docs;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const PayrollPayment = {
    print: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollPayment/print/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
    printPdf: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollPayment/printPdf/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
    downloadAll: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollPayment/downloadAll/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const Incentive = {
    getReport: async ({ fromDate, toDate, incentiveType }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/incentive/getReport`,
          getHeader("POST", { fromDate, toDate, incentiveType })
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
  };

  const Wallet = {
    getOpeningBalance: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/wallet/getOpeningBalance/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    setActive: async (id, isActive) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/wallet/setActive/${id}`,
          getHeader("POST", {
            isActive,
          })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getReport: async () => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/wallet/getReport`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const TopRanking = {
    getStats: async (period, showOnHomePage) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/topRanking/getStats`,
          getHeader("POST", {
            period,
            showOnHomePage,
          })
        );

        result = await result.json();
        // console.log("top ranking get stats", result);
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const CalendarEvent = {
    getByMonth: async (d) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/calendarEvent/getByMonth`,
          getHeader("POST", {
            date: d,
          })
        );
        result = await result.json();
        // console.log("Calendar events", result);
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const User = {
    setPassword: async (id, password) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/setPassword/${id}`,
          getHeader("POST", { password })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
    signIn: async (email, password) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/authenticate`,
          getHeader("POST", {
            email,
            password,
          })
        );
        // console.log(result);
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    resetPassword: async (email) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/resetPassword`,
          getHeader("POST", {
            email,
          })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getListGroupByRole: async () => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/getListGroupByRole/`,
          getHeader("GET")
        );
        result = await result.json();
        // console.log("API User getListGroupByRole ",result)
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    getAllFunctions: async () => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/userRole/allFunction`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data.functions ?? [];
      } catch (error) {
        console.log(error);
      }
    },

    getPortalLogin: async (userId, providerId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/getPortalLogin/${userId}/${providerId}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    importPortalLoginBatch: async (file, importData = {}) => {
      try {
        const data = new FormData();
        data.append("file", file);
        data.append("filename", file.name);
        data.append("token", getAuthToken());
        data.append("importData", JSON.stringify(importData));

        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/importPortalLoginBatch`,
          {
            method: "POST",
            body: data,
            redirect: "follow",
          }
        );
        const json = await result.json();
        if (json.error) throw json.error;
        return json;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    transferClients: async ({
      userId,
      consultantId,
      clientIds,
      transferBidOwnership,
    }) => {
      try {
        // console.log("api Transfer Clients", {
        //   userId,
        //   consultantId,
        //   clientIds,
        //   transferBidOwnership,
        // });
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/transferClients`,
          getHeader("POST", {
            userId,
            consultantId,
            clientIds,
            transferBidOwnership,
          })
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    getNonReleaseCommissionBidsCount: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/getNonReleaseCommissionBidsCount/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    getReport: async () => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/getReport`,
          getHeader("GET")
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getLoginActivityReport: async ({ fromDate, toDate }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/getLoginActivityReport`,
          getHeader("POST", { fromDate, toDate })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    logout: async () => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/logout`,
          getHeader("POST")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getNameCardReport: async () => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/getNameCardReport`,
          getHeader("GET")
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getCommissionOverridingReport: async () => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/getCommissionOverridingReport`,
          getHeader("GET")
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getNonSettledBidsCount: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/getNonSettledBidsCount/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    getProducerRate: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/getProducerRate/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        throw error;
      }
    },

    getClients: async (id, soleAgent = "true") => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/getClients/${id}/${soleAgent}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    terminate: async (termination) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/terminate`,
          getHeader("POST", { termination })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    changePassword: async ({ id, oldPassword, newPassword, newPassword2 }) => {
      try {
        // console.log(id, oldPassword, newPassword, newPassword2);

        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/changePassword`,
          getHeader("POST", {
            id,
            oldPassword,
            newPassword,
            newPassword2,
          })
        );
        result = await result.json();
        return result;
      } catch (error) {
        throw error;
      }
    },

    checkLicence: async (id, productTypeId) => {
      try {
        if (!id || !productTypeId) return;

        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/checkLicence`,
          getHeader("POST", {
            id,
            productType: productTypeId,
          })
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        throw error;
      }
    },

    setStatus: async (id, status) => {
      try {
        if (!status || !id) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/setStatus/${id}`,
          getHeader("POST", {
            status,
          })
        );
        result = await result.json();
        return result;
      } catch (error) {
        throw error;
      }
    },

    getWallets: async (userId) => {
      try {
        if (!userId) return [];
        const result = await search({
          apiEntity: "wallet",
          searchQuery: {
            $or: [
              { user: userId },
              {
                "members.user": {
                  $in: [userId],
                },
              },
            ],
          },
        });

        // console.log("FetchWallet", result);
        return result.docs ?? [];
      } catch (error) {
        console.log(error);
      }
    },

    getForcastPayroll: async (userId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/getForcastPayroll/${userId}`,
          getHeader("POST", {
            period: 1, //1year
          })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getOverridingPlan: async (userId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/getOverridingPlan/${userId}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    getAllTeamMembers: async (userId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/getAllTeamMembers/${userId}`,
          getHeader("GET")
        );
        result = await result.json();
        const data = result.data;
        return data?.teamMembers?.map((m) => m._id) ?? [];
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    getTeamMembers: async (userId, numlevel = 2) => {
      try {
        if (!userId) return [];

        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/getTeamMembers/${userId}`,
          getHeader("POST", {
            numlevel, // Number of level
          })
        );

        result = await result.json();
        return result.data.teamMembers ?? [];
      } catch (error) {
        console.log(error);
      }
    },

    getUplineMembers: async (userId, numlevel = 5) => {
      try {
        if (!userId) return [];

        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/getUplineMembers/${userId}`,
          getHeader("POST", {
            numlevel,
          })
        );

        result = await result.json();
        return result.data.uplineMembers ?? [];
      } catch (error) {
        console.log(error);
      }
    },
    getOrderStatics: async (userId, period) => {
      try {
        if (!userId) return;

        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/getOrderStatics/${userId}`,
          getHeader("POST", {
            period, //
          })
        );

        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    disableCreateOrder: async (userId, disabled) => {
      try {
        console.log("disabled", disabled);
        if (!userId) return;

        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/user/disableCreateOrder/${userId}`,
          getHeader("POST", {
            disabled, //
          })
        );

        result = await result.json();

        return result;
      } catch (error) {}
    },
  };

  const ConsultantIncentive = {
    getLatest: async () => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/consultantIncentive/getLatest`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    getByMonth: async (date) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/consultantIncentive/getByMonth`,
          getHeader("POST", { date })
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const ClientPromotion = {
    getLatest: async () => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/clientPromotion/getLatest`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    getByMonth: async (date) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/clientPromotion/getByMonth`,
          getHeader("POST", { date })
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const Bid = {
    getStatByWallet: async (walletId) => {
      // console.log("getStatByWallet", walletId);
      if (!walletId) return null;
      let result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/bid/getStatByWallet/${walletId}`,
        getHeader("GET")
      );
      result = await result.json();
      return result;
    },
    getByDebitNote: async (debitNoteId, params = {}) => {
      try {
        const qs = new URLSearchParams();
        if (params.orderId) qs.set("orderId", params.orderId);
        const query = qs.toString();
        const url = `${process.env.REACT_APP_SERVER_URL}/bid/getByDebitNote/${debitNoteId}${query ? `?${query}` : ""}`;
        let result = await fetch(url, getHeader("GET"));
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
  };
  const Ticket = {
    getByUser: async (id) => {
      try {
        if (!id) return null;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/ticket/getByUser/${id}`,
          getHeader("GET")
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    create: async (values) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/ticket/create`,
          getHeader("POST", values)
        );
        result = await result.json();
        // console.log(result);
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    addMessage: async (id, message) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/ticket/addMessage/${id}`,
          getHeader("POST", { message })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    updateTicketStatus: async (id, status) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/ticket/updateTicketStatus`,
          getHeader("POST", { status, id })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    updateTicketMembers: async (id, members) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/ticket/updateTicketMembers`,
          getHeader("POST", { members, id })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    // getMessages: async (id) => {
    //     if (!id) return null;
    //     let result = await fetch(
    //         `${process.env.REACT_APP_SERVER_URL}/ticket/getMessages/${id}`,
    //         getHeader("GET")
    //     );

    //     result = await result.json();
    //     return result;
    // },
  };

  const QuoRequest = {
    getStatSummary: async () => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quoRequest/getStatSummary/`,
          getHeader("GET")
        );

        result = await result.json();

        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    getStatSummaryByConsultant: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quoRequest/getStatSummaryByConsultant/${id}`,
          getHeader("GET")
        );

        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    copyToDN: async (values) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quoRequest/copyToDN/${values._id}`,
          getHeader("POST", { values })
        );

        result = await result.json();
        // console.log(result);
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
  };
  const Client = {
    getReport: async ({ fromDate, toDate }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/client/getReport`,
          getHeader("POST", { fromDate, toDate })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getConsultantReport: async () => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/client/getConsultantReport`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getAvailableCredit: async (id) => {
      try {
        if (!id) return null;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/client/getCredit/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        if (!result.success) throw result;
        return result.data.credit;
      } catch (error) {
        console.log(error);
      }
    },

    printWaitingStatement: async (id, type = "pdf") => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/client/printWaitingStatement/${id}/${type}`,
          getHeader("GET")
        );
        result = await result.json();

        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getPolicyReviewReport: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/client/getPolicyReviewReport/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getOrderSummary: async (id) => {
      try {
        if (!id) return null;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/client/getOrderSummary/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getRiskStats: async (id) => {
      try {
        if (!id) return null;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/client/getRiskStats/${id}`,
          getHeader("GET")
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getBirthdays: async (id, currentDate) => {
      try {
        if (!id) return null;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/client/getBirthdays/${id}`,
          getHeader("POST", {
            date: currentDate,
          })
        );

        result = await result.json();
        // console.log("getBirthdays", result);
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getRiskRPQ: async (id) => {
      try {
        if (!id) return null;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/client/getRiskRPQ/${id}`,
          getHeader("GET")
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    changeServiceRight: async ({ id, files, consultants }) => {
      // console.log("changeServiceRight ", id , consultants)
      try {
        if (!id) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/client/changeServiceRight`,
          getHeader("POST", {
            id,
            consultants,
            files,
          })
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getRiskAML: async (id) => {
      try {
        if (!id) return null;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/client/getRiskAML/${id}`,
          getHeader("GET")
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getStatSummary: async (preQuery) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/client/getStatSummary`,
          getHeader("POST", { preQuery })
        );

        result = await result.json();
        // console.log("Client getStatSummary >", result);
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    getStatSummaryByConsultant: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/client/getStatSummaryByConsultant/${id}`,
          getHeader("GET")
        );

        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    updateRiskAML: async (id, aml) => {
      try {
        await updateById({ apiEntity: "client", values: { aml: aml }, id });
      } catch (error) {
        console.log(error);
      }
    },

    updateRiskRPQ: async (id, rpq) => {
      try {
        await updateById({ apiEntity: "client", values: { rpq: rpq }, id });
      } catch (error) {
        console.log(error);
      }
    },

    refundCredit: async (clientId, credit, remark) => {
      try {
        if (!clientId || !credit) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/client/refundCredit`,
          getHeader("POST", { clientId, credit, remark })
        );
        result = await result.json();
        if (result.success) {
          showNotification({
            title: "Refund Credit Success",
            message: "Credit refunded successfully",
            color: "green",
          });
        }
        return result.data;
      } catch (error) {
        console.log(error);
        showNotification({
          title: "Refund Credit Failed",
          message: error?.message,
          color: "red",
        });
      }
    },

    transferCredit: async (fromClient, toClient, credit) => {
      try {
        if (!fromClient || !toClient || !credit) return;
        if (fromClient === toClient) {
          showNotification({
            title: "Transfer Credit Failed",
            message: "You cannot transfer credit to the same client",
            color: "red",
          });
        }
        // console.log("transferCredit", fromClient, toClient, credit);
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/client/transferCredit`,
          getHeader("POST", { fromClient, toClient, credit })
        );
        result = await result.json();
        // console.log("transferCredit result", result);
        if (result.success) {
          showNotification({
            title: "Transfer Credit Success",
            message: "Credit transferred successfully",
            color: "green",
          });
        }
        return result.data;
      } catch (error) {
        console.log(error);
        showNotification({
          title: "Transfer Credit Failed",
          message: error?.message,
          color: "red",
        });
      }
    },

    importLogBatch: async (file) => {
      try {
        const data = new FormData();
        data.append("file", file);
        data.append("filename", file.name);
        data.append("token", getAuthToken());

        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/client/importLogBatch`,
          {
            method: "POST",
            body: data,
            redirect: "follow",
          }
        );
        const json = await result.json();
        if (json.error) throw json.error;
        return json;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
  };

  const ClientRelationship = {
    // Get all relationships for a specific client
    getByClient: async (clientId) => {
      try {
        if (!clientId) return null;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/clientRelationship/client/${clientId}`,
          getHeader("GET")
        );
        result = await result.json();
        if (!result.success) throw result;
        return result.data;
      } catch (error) {
        console.log(error);
        return null;
      }
    },

    // Create a new client relationship
    create: async (data) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/clientRelationship/create`,
          getHeader("POST", data)
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return { success: false, message: error.message };
      }
    },

    // Update an existing client relationship
    update: async (id, data) => {
      try {
        if (!id) return null;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/clientRelationship/update/${id}`,
          getHeader("PUT", data)
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return { success: false, message: error.message };
      }
    },

    // Delete a client relationship
    delete: async (id) => {
      try {
        if (!id) return null;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/clientRelationship/delete/${id}`,
          getHeader("DELETE")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return { success: false, message: error.message };
      }
    },

    // Get relationship network for a client
    getNetwork: async (clientId) => {
      try {
        if (!clientId) return null;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/clientRelationship/network/${clientId}`,
          getHeader("GET")
        );
        result = await result.json();
        if (!result.success) throw result;
        return result.data;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
  };

  const Renewal = {
    getPendingRenewalReport: async (params) => {
      try {
        let url;
        if (typeof params === "string") {
          url = `${process.env.REACT_APP_SERVER_URL}/renewal/getPendingRenewalReport/${params}`;
        } else if (params && params.startDate && params.endDate) {
          const queryParams = new URLSearchParams({
            startDate: params.startDate,
            endDate: params.endDate,
          });
          url = `${process.env.REACT_APP_SERVER_URL}/renewal/getPendingRenewalReport/custom?${queryParams}`;
        } else {
          throw new Error(
            "Invalid parameters. Expected month string or { startDate, endDate } object"
          );
        }
        let result = await fetch(url, getHeader("GET"));
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getPendingRenewalsByUser: async (params) => {
      try {
        const includeTeamPending =
          process.env.REACT_APP_DEBIT_NOTE_SEARCH_TEAM === "true";
        // Support both old format (month string) and new format ({ startDate, endDate, page?, limit?, tab? })
        let url;
        if (typeof params === "string") {
          // Old format: month string like "2026-03" (backward compatible, no pagination)
          url = `${process.env.REACT_APP_SERVER_URL}/renewal/getPendingRenewalsByUser/${params}`;
          if (includeTeamPending) {
            url += "?includeTeam=true";
          }
        } else if (params && params.startDate && params.endDate) {
          // New format: { startDate, endDate, optional: page, limit, tab }
          const queryParams = new URLSearchParams({
            startDate: params.startDate,
            endDate: params.endDate,
          });
          // Add optional pagination and tab filter params
          if (params.page !== undefined) queryParams.set("page", String(params.page));
          if (params.limit !== undefined) queryParams.set("limit", String(params.limit));
          if (params.tab !== undefined) queryParams.set("tab", params.tab);
          if (params.search !== undefined && String(params.search).trim() !== "") {
            queryParams.set("search", String(params.search).trim());
          }
          if (includeTeamPending) queryParams.set("includeTeam", "true");
          // Use "custom" as the month param when using query params
          url = `${process.env.REACT_APP_SERVER_URL}/renewal/getPendingRenewalsByUser/custom?${queryParams}`;
        } else {
          throw new Error("Invalid parameters. Expected month string or { startDate, endDate } object");
        }
        let result = await fetch(url, getHeader("GET"));
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getPendingRenewalsForManagement: async (params) => {
      try {
        // Support both old format (month string) and new format ({ startDate, endDate, page?, limit?, tab? })
        let url;
        if (typeof params === "string") {
          // Old format: month string like "2026-03" (backward compatible, no pagination)
          url = `${process.env.REACT_APP_SERVER_URL}/renewal/getPendingRenewalsForManagement/${params}`;
        } else if (params && params.startDate && params.endDate) {
          // New format: { startDate, endDate, optional: page, limit, tab }
          const queryParams = new URLSearchParams({
            startDate: params.startDate,
            endDate: params.endDate,
          });
          // Add optional pagination and tab filter params
          if (params.page !== undefined) queryParams.set("page", String(params.page));
          if (params.limit !== undefined) queryParams.set("limit", String(params.limit));
          if (params.tab !== undefined) queryParams.set("tab", params.tab);
          if (params.search !== undefined && String(params.search).trim() !== "") {
            queryParams.set("search", String(params.search).trim());
          }
          // Use "custom" as the month param when using query params
          url = `${process.env.REACT_APP_SERVER_URL}/renewal/getPendingRenewalsForManagement/custom?${queryParams}`;
        } else {
          throw new Error("Invalid parameters. Expected month string or { startDate, endDate } object");
        }
        let result = await fetch(url, getHeader("GET"));
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    createManualFromDebitNote: async (params) => {
      try {
        const includeTeamPending =
          process.env.REACT_APP_DEBIT_NOTE_SEARCH_TEAM === "true";
        const url = `${process.env.REACT_APP_SERVER_URL}/renewal/createManualFromDebitNote`;
        const body = {
          debitNoteId: params.debitNoteId,
          startDate: params.startDate,
          endDate: params.endDate,
        };
        if (includeTeamPending) {
          body.includeTeam = true;
        }
        let result = await fetch(url, getHeader("POST", body));
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getRelatedDebitNotes: async ({ id }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/renewal/getRelatedDebitNotes/${id}`,
          getHeader("GET"),
        );
        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);

        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    getPendingRenewalTabStatsByUser: async (params) => {
      try {
        const includeTeamPending =
          process.env.REACT_APP_DEBIT_NOTE_SEARCH_TEAM === "true";
        let url;
        if (typeof params === "string") {
          url = `${process.env.REACT_APP_SERVER_URL}/renewal/getPendingRenewalTabStatsByUser/${params}`;
          if (includeTeamPending) {
            url += "?includeTeam=true";
          }
        } else if (params && params.startDate && params.endDate) {
          const queryParams = new URLSearchParams({
            startDate: params.startDate,
            endDate: params.endDate,
          });
          if (params.search !== undefined && String(params.search).trim() !== "") {
            queryParams.set("search", String(params.search).trim());
          }
          if (includeTeamPending) queryParams.set("includeTeam", "true");
          url = `${process.env.REACT_APP_SERVER_URL}/renewal/getPendingRenewalTabStatsByUser/custom?${queryParams}`;
        } else {
          throw new Error(
            "Invalid parameters. Expected month string or { startDate, endDate } object",
          );
        }
        let result = await fetch(url, getHeader("GET"));
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getPendingRenewalTabStatsForManagement: async (params) => {
      try {
        let url;
        if (typeof params === "string") {
          url = `${process.env.REACT_APP_SERVER_URL}/renewal/getPendingRenewalTabStatsForManagement/${params}`;
        } else if (params && params.startDate && params.endDate) {
          const queryParams = new URLSearchParams({
            startDate: params.startDate,
            endDate: params.endDate,
          });
          if (params.search !== undefined && String(params.search).trim() !== "") {
            queryParams.set("search", String(params.search).trim());
          }
          url = `${process.env.REACT_APP_SERVER_URL}/renewal/getPendingRenewalTabStatsForManagement/custom?${queryParams}`;
        } else {
          throw new Error(
            "Invalid parameters. Expected month string or { startDate, endDate } object",
          );
        }
        let result = await fetch(url, getHeader("GET"));
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getPendingRenewalReportByUser: async (params) => {
      try {
        const includeTeamPending =
          process.env.REACT_APP_DEBIT_NOTE_SEARCH_TEAM === "true";
        // Support both old format (month string) and new format ({ startDate, endDate })
        let url;
        if (typeof params === "string") {
          // Old format: month string like "2026-03"
          url = `${process.env.REACT_APP_SERVER_URL}/renewal/getPendingRenewalReportByUser/${params}`;
          if (includeTeamPending) {
            url += "?includeTeam=true";
          }
        } else if (params && params.startDate && params.endDate) {
          // New format: { startDate, endDate }
          const queryParams = new URLSearchParams({
            startDate: params.startDate,
            endDate: params.endDate,
          });
          if (includeTeamPending) queryParams.set("includeTeam", "true");
          url = `${process.env.REACT_APP_SERVER_URL}/renewal/getPendingRenewalReportByUser/custom?${queryParams}`;
        } else {
          throw new Error("Invalid parameters. Expected month string or { startDate, endDate } object");
        }
        let result = await fetch(url, getHeader("GET"));
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getReport: async (month) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/renewal/getReport/${month}`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getConsultantReport: async (month) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/renewal/getConsultantReport/${month}`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    print: async (id, format = "pdf") => {
      //Quo : Quotation, RN: Renewal Notice
      try {
        if (!id) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/renewal/print/${id}/${format}`,
          getHeader("GET")
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getStatSummary: async (preQuery) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/renewal/getStatSummary`,
          getHeader("POST", { preQuery })
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getGiRenewalDashboardStats: async (period, userId = null) => {
      try {
        if (!period?.startDate || !period?.endDate) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/renewal/getGiRenewalDashboardStats`,
          getHeader("POST", { period, userId })
        );

        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);

        return result?.data;
      } catch (error) {
        console.log(error);
      }
    },

    printCancelNotice: async (id) => {
      //Quo : Quotation, RN: Renewal Notice
      try {
        if (!id) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/renewal/printCancelNotice/${id}`,
          getHeader("GET")
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    confirm: async (id) => {
      try {
        if (!id) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/renewal/confirm/${id}`,
          getHeader("GET")
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
    reinstate: async (id) => {
      try {
        if (!id) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/renewal/reinstate/${id}`,
          getHeader("POST", {})
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
    copyToDN: async (values) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/renewal/copyToDN/${values._id}`,
          getHeader("POST", { values })
        );

        return await result.json();
      } catch (error) {
        console.log(error);
      }
    },
  };

  const Quotation = {
    print: async (id, type = "QUO", format = "pdf") => {
      //Quo : Quotation, RN: Renewal Notice
      try {
        if (!id) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quotation/print/${id}/${type}/${format}`,
          getHeader("GET")
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
    copyToDN: async (values) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quotation/copyToDN/${values._id}`,
          getHeader("POST", { values })
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return { success: false, error };
      }
    },

    getStatSummary: async (preQuery) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quotation/getStatSummary`,
          getHeader("POST", { preQuery })
        );
        // console.log("API quotation getStatSummary ", result);
        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);

        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    confirm: async (id, format = "pdf") => {
      try {
        if (!id) return;
        const fmt = format === "word" ? "word" : "pdf";
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quotation/confirm/${id}?format=${fmt}`,
          getHeader("GET")
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const CreditNote = {
    print: async (id, type = "pdf") => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/creditNote/print/${id}/${type}`,
          getHeader("GET")
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
    getReport: async ({ fromDate, toDate }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/creditNote/getReport`,
          getHeader("POST", { fromDate, toDate })
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getStatSummary: async () => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/creditNote/getStatSummary/`,
          getHeader("GET")
        );
        // console.log("API order getStatSummary ", result);
        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);

        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    getCommissionBids: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/creditNote/getCommissionBids/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    confirmSettlement: async ({ id, settlement }) => {
      try {
        if (!id) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/creditNote/confirmSettlement`,
          getHeader("POST", {
            settlement,
            id,
          })
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    unSettle: async (id) => {
      try {
        if (!id) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/creditNote/unSettle/${id}`,
          getHeader("GET")
        );

        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);
        return result;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
  };

  const DebitNote = {
    print: async (id, type = "pdf") => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/print/${id}/${type}`,
          getHeader("GET")
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
    printWord: async (id, type = "pdf") => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/printWord/${id}/${type}?tpl=word`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
    getPendingRenewalReport: async (params) => {
      try {
        let url;
        if (typeof params === "string") {
          url = `${process.env.REACT_APP_SERVER_URL}/renewal/getPendingRenewalReport/${params}`;
        } else if (params && params.startDate && params.endDate) {
          const queryParams = new URLSearchParams({
            startDate: params.startDate,
            endDate: params.endDate,
          });
          url = `${process.env.REACT_APP_SERVER_URL}/renewal/getPendingRenewalReport/custom?${queryParams}`;
        } else {
          throw new Error(
            "Invalid parameters. Expected month string or { startDate, endDate } object"
          );
        }
        let result = await fetch(url, getHeader("GET"));
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getOutStandingDocumentReport: async ({ consultant }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/getOutStandingDocumentReport`,
          getHeader("POST", { consultant })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
    printReceipt: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/printReceipt/${id}`,
          getHeader("GET")
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getStatSummary: async (preQuery) => {
      try {
        // console.log("getStatSummary", preQuery);
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/getStatSummary`,
          getHeader("POST", { preQuery })
        );
        result = await result.json();
        // console.log("DN getStatSummary ", result);
        if (!result || !result.success) return handleResponseError(result);

        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    getOutStandingPaymentReport: async ({ consultant }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/getOutStandingPaymentReport`,
          getHeader("POST", { consultant })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getGiOverdueConsultantDashboard: async ({
      paymentStatus,
      status,
      policyStatus,
      scopeToSelf,
      includeTeam,
    }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/getGiOverdueConsultantDashboard`,
          getHeader("POST", {
            paymentStatus,
            status,
            policyStatus,
            scopeToSelf,
            includeTeam,
          })
        );
        result = await result.json();
        handleResponseError(result);
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getGiOverdueConsultantDashboardReport: async ({
      paymentStatus,
      status,
      policyStatus,
      scopeToSelf,
      includeTeam,
    }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/getGiOverdueConsultantDashboardReport`,
          getHeader("POST", {
            paymentStatus,
            status,
            policyStatus,
            scopeToSelf,
            includeTeam,
          })
        );
        result = await result.json();
        handleResponseError(result);
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getAccountPayableInsurerReport: async ({ fromDate, toDate, onlyPaid }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/getAccountPayableInsurerReport`,
          getHeader("POST", { fromDate, toDate, onlyPaid })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getPendingSettlementReport: async ({ includePendingPayment }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/getPendingSettlementReport`,
          getHeader("POST", { includePendingPayment })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    confirmPayment: async ({ id, payment }) => {
      try {
        if (!id) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/confirmPayment`,
          getHeader("POST", {
            payment,
            id,
          })
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
    unconfirmPayment: async ({ id }) => {
      try {
        if (!id) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/unconfirmPayment/${id}`,
          getHeader("GET")
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    holdCover: async ({ id, holdCover }) => {
      try {
        if (!id) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/holdCover`,
          getHeader("POST", {
            holdCover,
            id,
          })
        );

        result = await result.json();
        return result;
      } catch (error) {
        throw error;
      }
    },
    confirmPolicy: async ({ id, dn }) => {
      try {
        if (!id) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/confirmPolicy`,
          getHeader("POST", {
            id,
            policyNumber: dn.policyNumber,
            confirmedAt: dn.confirmedAt,
          })
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    confirmSettlement: async ({ id, settlement }) => {
      try {
        if (!id) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/confirmSettlement`,
          getHeader("POST", {
            settlement,
            id,
          })
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    unSettle: async (id) => {
      try {
        if (!id) return;
        console.log("unSettle", id);
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/unSettle/${id}`,
          getHeader("GET")
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getReport: async ({
      fromDate,
      toDate,
      dateType,
      consultant,
      policyStatus,
    }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/getReport`,
          getHeader("POST", {
            fromDate,
            toDate,
            dateType,
            consultant,
            policyStatus,
          })
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getConsultantReport: async ({
      fromDate,
      toDate,
      dateType,
      policyStatus,
    }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/getConsultantReport`,
          getHeader("POST", {
            fromDate,
            toDate,
            dateType,
            policyStatus,
          })
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    refundExcess: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/refundExcess/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    transferExcessToCredit: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/transferExcessToCredit/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    addEndorsement: async ({ endorsement, dn }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/addEndorsement`,
          getHeader("POST", { endorsement, dn })
        );
        result = await result.json();
        if (!result || !result.success) throw result.error;

        // console.log("DebitNote addEndorsement", result);
        return result;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    getEndorsements: async ({ id }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/getEndorsements/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);

        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    getRelatedDebitNotes: async ({ id }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/getRelatedDebitNotes/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);

        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    deleteEndorsement: async ({ id }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/debitNote/deleteEndorsement/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);

        return result;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
  };

  const Cpd = {
    getUserDetail: async ({ year, userId, type }) => {
      // console.log("getUserDetail", year, userId);
      try {
        if (!userId) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/cpd/getUserDetail`,
          getHeader("POST", {
            year,
            userId,
            type,
          })
        );

        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);

        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getIAYearReport: async (year, { skipExport = false } = {}) => {
      try {
        if (!year) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/cpd/getIAYearReport`,
          getHeader("POST", {
            year,
            skipExport,
          })
        );
        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);

        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getMPFYearReport: async (year, { skipExport = false } = {}) => {
      try {
        if (!year) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/cpd/getMPFYearReport`,
          getHeader("POST", {
            year,
            skipExport,
          })
        );
        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);

        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getSFCYearReport: async (year, { skipExport = false } = {}) => {
      try {
        if (!year) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/cpd/getSFCYearReport`,
          getHeader("POST", {
            year,
            skipExport,
          })
        );
        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);

        return result;
      } catch (error) {
        console.log(error);
      }
    },

    parseCertificate: async ({ fileLink }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/cpd/parseCertificate`,
          getHeader("POST", { fileLink })
        );
        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    submitConsultantBatch: async ({ records }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/cpd/submitConsultantBatch`,
          getHeader("POST", { records })
        );
        result = await result.json();
        if (!result || !result.success) {
          handleResponseError(result);
          throw result?.error;
        }
        return result;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
  };

  const OrderService = {
    completeService: async ({ id, submissionDate, receieveDate, completeDate }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/orderService/completeService`,
          getHeader("POST", {
            id,
            submissionDate,
            receieveDate,
            completeDate,
          })
        );

        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);

        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    printSubmitCoverSheet: async (orderService) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/orderService/printCoverSheet`,
          getHeader("POST", {
            service: orderService,
          })
        );

        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);

        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    getReport: async ({ fromDate, toDate }) => {
      try {
        // console.log("OrderService get Report");

        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/orderService/getReport`,
          getHeader("POST", {
            fromDate,
            toDate,
          })
        );

        result = await result.json();
        // console.log(result);
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getConsultantReport: async ({ fromDate, toDate }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/orderService/getConsultantReport`,
          getHeader("POST", {
            fromDate,
            toDate,
          })
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const OrderInvestmentTransaction = {
    createForOrder: async ({ orderId }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/orderInvestmentTransaction/createForOrder`,
          getHeader("POST", { orderId })
        );
        result = await result.json();
        if (!result || !result.success) {
          handleResponseError(result);
          const message =
            (typeof result?.error === "string" && result.error) ||
            result?.error?.message ||
            result?.message ||
            "Failed to create transaction";
          throw new Error(message);
        }
        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    printArForm: async (transactionId, arForm) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/orderInvestmentTransaction/printArForm/${transactionId}`,
          getHeader("POST", { arForm })
        );
        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);
        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
  };

  const Order = {
    getStatSummaryByConsultant: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/getStatSummaryByConsultant/${id}`,
          getHeader("GET")
        );
        // console.log("getStatSummaryByConsultant", result);
        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);

        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    getRenewalReport: async (yyyymm, allPaymentTerm = false) => {
      try {
        // console.log("getRenewalReport", yyyymm, allPaymentTerm);
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/getRenewalReport`,
          getHeader("POST", { yyyymm, allPaymentTerm })
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    getPendingCaseReport: async ({ fromDate, toDate }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/getPendingCaseReport`,
          getHeader("POST", { fromDate, toDate })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getReport: async ({ fromDate, toDate, dateType }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/getReport`,
          getHeader("POST", {
            fromDate,
            toDate,
            dateType,
          })
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getConsultantReport: async ({ fromDate, toDate, dateType }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/getConsultantReport`,
          getHeader("POST", {
            fromDate,
            toDate,
            dateType,
          })
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getConsultantPendingCaseReport: async ({ fromDate, toDate }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/getConsultantPendingCaseReport`,
          getHeader("POST", { fromDate, toDate })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getConsultantRenewalReport: async (yyyymm, allPaymentTerm = false) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/getConsultantRenewalReport`,
          getHeader("POST", { yyyymm, allPaymentTerm })
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    getIAReportEncl2: async ({ fromDate, toDate }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/getIAReportEncl2`,
          getHeader("POST", {
            fromDate,
            toDate,
          })
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getIAReportEncl10: async ({ fromDate, toDate }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/getIAReportEncl10`,
          getHeader("POST", {
            fromDate,
            toDate,
          })
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getRenewal: async (period, consultantId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/getRenewal`,
          getHeader("POST", { period, consultant: consultantId })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getStatSummary: async (preQuery) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/getStatSummary`,
          getHeader("POST", { preQuery })
        );
        // console.log("API order getStatSummary ", result);
        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);

        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    addRider: async ({ riderValue, basicPlanOrderId }) => {
      // console.log("Userserver add rider", riderValue, basicPlanOrderId);
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/addRider`,
          getHeader("POST", {
            riderValue,
            basicPlanOrderId,
          })
        );
        result = await result.json();
        // console.log("API order addRider", result);
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    addService: async ({ service }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/addService`,
          getHeader("POST", {
            service,
          })
        );
        result = await result.json();
        if (!result || !result.success) {
          handleResponseError(result);
          const err = new Error(
            result?.data?.error?.message ??
              result?.data?.error ??
              result?.message ??
              "Failed to add service"
          );
          err.data = result?.data;
          throw err;
        }
        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
    getStats: async (
      period,
      userId,
      dateType = "signedDate",
      statuses,
      productCategoryId
    ) => {
      try {
        if (!period.startDate || !period.endDate || !dateType) return;
        // console.log("Order getStats", period, userId, dateType);
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/getStats`,
          getHeader("POST", {
            period,
            userId,
            dateType,
            statuses,
            productCategoryId,
          })
        );

        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);

        return result?.data;
      } catch (error) {
        console.log(error);
      }
    },

    getYearComparisonStats: async (
      userId,
      dateType = "signedDate",
      statuses,
      years,
      productCategoryId
    ) => {
      try {
        if (!dateType) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/getYearComparisonStats`,
          getHeader("POST", {
            userId,
            dateType,
            statuses,
            years,
            productCategoryId,
          })
        );

        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);

        return result?.data;
      } catch (error) {
        console.log(error);
      }
    },

    getTrChannelPerformanceStats: async (
      period,
      userId,
      dateType = "signedDate",
      statuses,
      productCategoryId
    ) => {
      try {
        if (!period?.startDate || !period?.endDate || !dateType) return;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/getTrChannelPerformanceStats`,
          getHeader("POST", {
            period,
            userId,
            dateType,
            statuses,
            productCategoryId,
          })
        );

        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);

        return result?.data;
      } catch (error) {
        console.log(error);
      }
    },

    getQuotation: async (orderId) => {
      try {
        let result = api.search({
          apiEntity: "quotation",
          pageSize: 20,
          currentPage: 1,
          searchQuery: {
            order: orderId,
          },
        });

        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);

        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    getInvoices: async (orderId) => {
      try {
        let result = api.search({
          apiEntity: "invoice",
          pageSize: 20,
          currentPage: 1,
          searchQuery: {
            order: orderId,
          },
        });

        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);

        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    importPolicyBeneficiaryBatch: async (file) => {
      try {
        const data = new FormData();
        data.append("file", file);
        data.append("filename", file.name);
        data.append("token", getAuthToken());

        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/importPolicyBeneficiaryBatch`,
          {
            method: "POST",
            body: data,
            redirect: "follow",
          }
        );
        const json = await result.json();
        if (json.error) throw json.error;
        return json;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    importPolicyInsuredBatch: async (file) => {
      try {
        const data = new FormData();
        data.append("file", file);
        data.append("filename", file.name);
        data.append("token", getAuthToken());

        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/importPolicyInsuredBatch`,
          {
            method: "POST",
            body: data,
            redirect: "follow",
          }
        );
        const json = await result.json();
        if (json.error) throw json.error;
        return json;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    receiveOrder: async (id, checkList, receivedDate) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/receiveOrder/${id}`,
          getHeader("POST", { checkList, receivedDate })
        );
        result = await result.json();
        // console.log("receiveOrder", result);
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    printSubmissionCoverSheet: async (
      orderId,
      checkList,
      type,
      submissionDate,
      receive = false
    ) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/printSubmissionCoverSheet/${orderId}`,
          getHeader("POST", {
            checkList,
            type,
            submissionDate,
            receive,
          })
        );
        result = await result.json();

        return result.data;
      } catch (error) {
        // console.log(error);
      }
    },

    confirmPolicy: async (orderId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/confirmPolicy/${orderId}`,
          getHeader("GET")
        );
        result = await result.json();
        // console.log("confirm policy", result);
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    generateCommissionPayment: async (orderId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/generateCommissionPayment/${orderId}`,
          getHeader("GET")
        );
        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);
        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },

    getCommissionPayment: async (orderId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/getCommissionPayment/${orderId}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    terminate: async (id, termination) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/order/terminate`,
          getHeader("POST", { id, termination })
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const CommissionPayment = {
    getCommissionPaymentReport: async ({ fromDate, toDate, dateType }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionPayment/getCommissionPaymentReport`,
          getHeader("POST", { fromDate, toDate, dateType })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
    getWaitingList: async (expectedPeriod) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionPayment/getWaitingList/${expectedPeriod}`,
          getHeader("GET")
        );
        result = await result.json();
        // console.log("getWaitingList", expectedPeriod, result);
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    skipPayment: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionPayment/skipPayment/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    waitPayment: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionPayment/waitPayment/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    resetPayment: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionPayment/resetPayment/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    skipOldWaitingPayment: async (orderId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionPayment/skipOldWaitingPayment/${orderId}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const CommissionSchedule = {
    import: async (file, id) => {
      try {
        if (!file || !id) return;
        let data = new FormData();
        data.append("file", file);
        data.append("filename", file.name); // use orginial name
        data.append("token", getAuthToken());
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionSchedule/import/${id}`,
          {
            method: "POST",
            body: data,
            redirect: "follow",
          }
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
    createProductCommissionTable: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionSchedule/createProductCommissionTable/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
    downloadTemplate: async (effectiveDate, filter = "all", providerId = null, productIds = []) => {
      try {
        let apiUrl = `${process.env.REACT_APP_SERVER_URL}/commissionSchedule/downloadTemplate`;
        const params = [];
        if (effectiveDate) {
          const dateStr = typeof effectiveDate === 'string' 
            ? effectiveDate 
            : effectiveDate.toISOString().split('T')[0];
          params.push(`effectiveDate=${dateStr}`);
        }
        if (filter && filter !== "all") {
          params.push(`filter=${filter}`);
        }
        if (providerId) {
          params.push(`provider=${providerId}`);
        }
        if (productIds && productIds.length > 0) {
          // Handle array of product IDs - extract _id from objects if needed
          const ids = productIds.map(p => typeof p === 'string' ? p : p._id).filter(Boolean);
          if (ids.length > 0) {
            params.push(`products=${ids.join(',')}`);
          }
        }
        if (params.length > 0) {
          apiUrl += `?${params.join("&")}`;
        }
        let result = await fetch(apiUrl, getHeader("GET"));
        if (!result.ok) {
          throw new Error("Failed to download template");
        }
        const blob = await result.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        const contentDisposition = result.headers.get("Content-Disposition");
        let fileName = "commissionScheduleTemplate.xlsx";
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=(.+)/);
          if (match) fileName = match[1];
        }
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        a.remove();
        return { success: true };
      } catch (error) {
        console.log(error);
        return { success: false, error: error.message };
      }
    },
    updateFilterState: async (id, filterData) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionSchedule/updateFilterState/${id}`,
          getHeader("PUT", filterData)
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return { success: false, error: error.message };
      }
    },
    getScheduleProductList: async (id, { page = 0, limit = 20, provider = null, search = null } = {}) => {
      try {
        if (!id) return null;
        const params = [`page=${page}`, `limit=${limit}`];
        if (provider) {
          params.push(`provider=${provider}`);
        }
        if (search && String(search).trim()) {
          params.push(`search=${encodeURIComponent(String(search).trim())}`);
        }
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionSchedule/getScheduleProductList/${id}?${params.join("&")}`,
          getHeader("GET")
        );
        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);
        return result.data;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    backfillScheduleProductCodes: async (id) => {
      try {
        if (!id) return null;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionSchedule/backfillScheduleProductCodes/${id}`,
          getHeader("POST", {})
        );
        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);
        return result.data;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
  };

  const CommissionStatement = {
    import: async (file) => {
      try {
        let data = new FormData();
        data.append("file", file);
        data.append("filename", file.name); // use orginial name
        data.append("token", getAuthToken());
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionStatement/import`,
          {
            method: "POST",
            body: data,
            redirect: "follow",
          }
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getReport: async ({ fromDate, toDate }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionStatement/getReport`,
          getHeader("POST", { fromDate, toDate })
        );
        result = await result.json();
        // console.log("get report", result);
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    downloadPendingCase: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionStatement/downloadPendingCase/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    verifyRow: async (row) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionStatement/verifyRow`,
          getHeader("POST", { row })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    remapRowOrder: async (row, orderId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionStatement/remapRowOrder`,
          getHeader("POST", { row, orderId })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    refreshAmbiguousCandidates: async (row) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionStatement/refreshAmbiguousCandidates`,
          getHeader("POST", { row })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    verifyRowAmount: async (row) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionStatement/verifyRowAmount`,
          getHeader("POST", { row })
        );
        result = await result.json();
        // console.log(result);
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    resetRow: async (row) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionStatement/resetRow`,
          getHeader("POST", { row })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    rowAcceptPayment: async (row) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionStatement/rowAcceptPayment`,
          getHeader("POST", { row })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getMissingPayment: async (period, rows) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionStatement/getMissingPayment`,
          getHeader("POST", { period, rows })
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    linkMorePayment: async (row) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionStatement/linkMorePayment`,
          getHeader("POST", { row })
        );
        result = await result.json();
        return result.data?.row;
      } catch (error) {
        console.log(error);
      }
    },
    rowAcceptAndCreateWaitingPaymentForRemaining: async (row) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionStatement/rowAcceptAndCreateWaitingPaymentForRemaining`,
          getHeader("POST", { row })
        );
        result = await result.json();
        return result.data?.row;
      } catch (error) {
        console.log(error);
      }
    },

    confirmAllVerifiedRow: async (id) => {
      try {
        if (!id) return null;
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionStatement/confirmAllVerifiedRow/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        // console.log(
        //     "%cuserServerApi.js line:422 result",
        //     "color: #007acc;",
        //     result
        // );
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    createWithBankTransaction: async (payload) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionStatement/createWithBankTransaction`,
          getHeader("POST", payload)
        );
        result = await result.json();
        if (!result.success) throw new Error(result.message || result.msg);
        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
  };

  const ClientPayment = {
    add: async ({ payment, debitNote }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/clientPayment/add`,
          getHeader("POST", { payment, debitNote })
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
    remove: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/clientPayment/remove/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        if (!result.success) {
          throw result.error || result.message || "Failed to delete client payment";
        }
        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
    getReport: async ({ fromDate, toDate, dateType }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/clientPayment/getReport`,
          getHeader("POST", { fromDate, toDate, dateType })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
    update: async (id, values) => {
      try {
        console.log("api update values", id, values);
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/clientPayment/update/${id}`,
          getHeader("POST", { values })
        );
        result = await result.json();
        console.log("api update result", result);
        if (!result.success) {
          throw result.error || result.message || "Failed to update client payment";
        }
        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
    printReceipt: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/clientPayment/printReceipt/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
  };

  const AIRequest = {
    getReport: async ({ fromDate, toDate }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/aiRequest/getReport`,
          getHeader("POST", { fromDate, toDate })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
  };

  const GISettlement = {
    getPending: async (id, provider) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/giSettlement/getPending/${id}`,
          getHeader("POST", { provider })
        );
        result = await result.json();
        // console.log("getPending", result);
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    reset: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/giSettlement/reset/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    confirm: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/giSettlement/confirm/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    print: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/giSettlement/print/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    removeDebitNote: async (debitNoteId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/giSettlement/removeDebitNote`,
          getHeader("POST", { debitNoteId })
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    importStatement: async (id, file) => {
      try {
        if (!file || !id) return;
        let data = new FormData();
        data.append("file", file);
        data.append("filename", file.name);
        data.append("token", getAuthToken());
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/giSettlement/importStatement/${id}`,
          {
            method: "POST",
            body: data,
            redirect: "follow",
          }
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
    crossCheck: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/giSettlement/crossCheck/${id}`,
          getHeader("POST")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
    downloadStatement: async (id, preferredFileName) => {
      try {
        if (!id) return { success: false, error: "No settlement id." };
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/giSettlement/downloadStatement/${id}`,
          getHeader("GET")
        );
        if (!result.ok) {
          const errorBody = await result.json().catch(() => null);
          throw new Error(errorBody?.message || "Failed to download statement");
        }
        const blob = await result.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        const contentDisposition = result.headers.get("Content-Disposition");
        let fileName = preferredFileName || "gi-settlement-statement.xlsx";
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=(.+)/);
          if (match) fileName = match[1];
        }
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        a.remove();
        return { success: true };
      } catch (error) {
        console.log(error);
        return { success: false, error: error.message };
      }
    },
    downloadCrossCheckResult: async (id, preferredFileName) => {
      try {
        if (!id) return { success: false, error: "No settlement id." };
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/giSettlement/downloadCrossCheckResult/${id}`,
          getHeader("GET")
        );
        if (!result.ok) {
          const errorBody = await result.json().catch(() => null);
          throw new Error(
            errorBody?.message || "Failed to download cross-check result"
          );
        }
        const blob = await result.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        const contentDisposition = result.headers.get("Content-Disposition");
        let fileName =
          preferredFileName || "gi-settlement-cross-check-result.xlsx";
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=(.+)/);
          if (match) fileName = match[1];
        }
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        a.remove();
        return { success: true };
      } catch (error) {
        console.log(error);
        return { success: false, error: error.message };
      }
    },
    downloadGiOverdueDashboardResult: async (yearMonth, preferredFileName) => {
      try {
        if (!yearMonth) {
          return { success: false, error: "No month selected." };
        }
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/giSettlement/downloadGiOverdueDashboardResult`,
          getHeader("POST", { yearMonth })
        );
        if (!result.ok) {
          const errorBody = await result.json().catch(() => null);
          throw new Error(
            errorBody?.message || "Failed to download overdue dashboard result"
          );
        }
        const blob = await result.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        const contentDisposition = result.headers.get("Content-Disposition");
        let fileName =
          preferredFileName || `gi-overdue-insurer-${yearMonth}.xlsx`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=(.+)/);
          if (match) fileName = match[1];
        }
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        a.remove();
        return { success: true };
      } catch (error) {
        console.log(error);
        return { success: false, error: error.message };
      }
    },
    getInsurerOutstandingSettlementReport: async ({ fromDate, toDate }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/giSettlement/getInsurerOutstandingSettlementReport`,
          getHeader("POST", { fromDate, toDate })
        );
        result = await result.json();
        handleResponseError(result);
        return result;
      } catch (error) {
        console.log(error);
      }
    },
    getGiOverdueDashboard: async ({ yearMonth }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/giSettlement/getGiOverdueDashboard`,
          getHeader("POST", { yearMonth })
        );
        result = await result.json();
        handleResponseError(result);
        return result;
      } catch (error) {
        console.log(error);
        return { success: false, error: error.message };
      }
    },
    downloadStatementTemplateUrl: `${process.env.REACT_APP_SERVER_URL}/giSettlement/downloadStatementTemplate`,
  };

  const BankAccount = {
    getMatchingTransaction: async (id, type) => {
      try {
        console.log("getMatchingTransaction", id);
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/bankAccount/getMatchingTransaction/${id}`,
          getHeader("POST", { type })
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    confirmMatching: async (matching, id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/bankAccount/confirmMatching/${id}`,
          getHeader("POST", { matching })
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const GIMatchPayment = {
    getUnMatch: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/giMatchPayment/getUnMatch/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        // console.log("getPending", result);
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    confirm: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/giMatchPayment/confirm/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const Payroll2 = {
    generatePayrollBids: async (payrollId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/generate/${payrollId}`,
          getHeader("POST", {})
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    getPayrollBidsSummary: async (payrollId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/getByPayroll/summary/${payrollId}`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    getPayrollBids: async (payrollId, params = {}) => {
      try {
        const qs = new URLSearchParams();
        if (params.page != null) qs.set("page", params.page);
        if (params.limit != null) qs.set("limit", params.limit);
        if (params.walletId) qs.set("walletId", params.walletId);
        if (params.status) qs.set("status", params.status);
        if (params.sourceId) qs.set("sourceId", params.sourceId);
        if (params.sourceType) qs.set("sourceType", params.sourceType);
        const query = qs.toString();
        const url = `${process.env.REACT_APP_SERVER_URL}/payrollBid/getByPayroll/${payrollId}${query ? `?${query}` : ""}`;
        let result = await fetch(url, getHeader("GET"));
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    getPayrollBidsByDebitNote: async (debitNoteId, params = {}) => {
      try {
        const qs = new URLSearchParams();
        if (params.orderId) qs.set("orderId", params.orderId);
        if (params.page != null) qs.set("page", params.page);
        if (params.limit != null) qs.set("limit", params.limit);
        const query = qs.toString();
        const url = `${process.env.REACT_APP_SERVER_URL}/payrollBid/getByDebitNote/${debitNoteId}${query ? `?${query}` : ""}`;
        let result = await fetch(url, getHeader("GET"));
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    getOrderGroupSummary: async (payrollId, params = {}) => {
      try {
        const query = new URLSearchParams();
        if (params.page != null) query.set("page", String(params.page));
        if (params.limit != null) query.set("limit", String(params.limit));
        if (params.includeIncentives === false) {
          query.set("includeIncentives", "false");
        }
        const qs = query.toString();
        const url = `${process.env.REACT_APP_SERVER_URL}/payrollBid/getByPayroll/orderSummary/${payrollId}${qs ? `?${qs}` : ""}`;
        let result = await fetch(url, getHeader("GET"));
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    getClientPaymentsForPayroll: async (fromDate, toDate, payrollId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/getClientPayments`,
          getHeader("POST", { fromDate, toDate, payrollId })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    getCommissionStatementsForPayroll: async (
      fromPeriod,
      toPeriod,
      payrollId
    ) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/getCommissionStatements`,
          getHeader("POST", { fromPeriod, toPeriod, payrollId })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    getIncentivesForPayroll: async (fromDate, toDate, payrollId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/getIncentives`,
          getHeader("POST", { fromDate, toDate, payrollId })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    getDebitNotesForPayroll: async (ids, searchQuery) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/getDebitNotes`,
          getHeader("POST", { ids, searchQuery })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    getOrdersForPayroll: async (ids) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/getOrders`,
          getHeader("POST", { ids })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    getOrdersTabTable: async (payrollId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/getOrdersTabTable`,
          getHeader("POST", { payrollId })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    getClientPaymentsByIds: async (ids) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/getClientPaymentsByIds`,
          getHeader("POST", { ids })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    updateBidStatus: async (bidId, status) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/updateStatus`,
          getHeader("POST", { bidId, status })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    holdByWallet: async (payrollId, walletId, hold) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/holdByWallet`,
          getHeader("POST", { payrollId, walletId, hold })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    holdBySource: async (payrollId, sourceId, sourceType, hold) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/holdBySource`,
          getHeader("POST", { payrollId, sourceId, sourceType, hold })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    holdBySources: async (payrollId, sources, hold) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/holdBySources`,
          getHeader("POST", { payrollId, sources, hold })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    bulkUpdateStatus: async (bidIds, status) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/bulkUpdateStatus`,
          getHeader("POST", { bidIds, status })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    getPayrollReport: async (payrollId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/getReport/${payrollId}`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    getHoldingBidsReport: async (payrollId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/getHoldingBidsReport/${payrollId}`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    getDuplicateBidConflicts: async (payrollId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/getDuplicateBidConflicts/${payrollId}`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    filterDispatchedSourceConflicts: async (
      payrollId,
      { debitNoteIds = [], orderIds = [], incentiveIds = [] } = {},
    ) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/filterDispatchedSourceConflicts/${payrollId}`,
          getHeader("POST", { debitNoteIds, orderIds, incentiveIds }),
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    getWalletReport: async (payrollId, { settledOnly } = {}) => {
      try {
        const query = settledOnly ? "?settledOnly=true" : "";
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/getWalletReport/${payrollId}${query}`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    getHsbcMriFile: async (payrollId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/getHsbcMriFile/${payrollId}`,
          getHeader("GET")
        );
        result = await result.json();
        if (!result?.success || !result?.data?.content) {
          return result;
        }

        const { content, filename, skipped, holdExcludedCount, lineCount, totalAmount } =
          result.data;
        const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename || "hsbc-mri.txt";
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        a.remove();

        return {
          success: true,
          data: { skipped, holdExcludedCount, lineCount, totalAmount },
        };
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    getIr56mXml: async (assessmentYear) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/getIr56mXml?assessmentYear=${assessmentYear}`,
          getHeader("GET")
        );
        result = await result.json();
        if (!result?.success || !result?.data?.content) {
          return result;
        }

        const {
          content,
          filename,
          meta,
          payrollCount,
          bidCount,
          includedCount,
          excludedCount,
          excluded,
        } = result.data;
        const blob = new Blob([content], {
          type: "application/xml;charset=utf-8",
        });
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename || `IR56M-${assessmentYear}.xml`;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        a.remove();

        return {
          success: true,
          data: {
            meta,
            payrollCount,
            bidCount,
            includedCount,
            excludedCount,
            excluded,
          },
        };
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    getIr56mExcel: async (assessmentYear) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/getIr56mExcel?assessmentYear=${assessmentYear}`,
          getHeader("GET")
        );
        result = await result.json();
        if (!result?.success || !result?.data?.url) {
          return result;
        }

        const link = document.createElement("a");
        link.href = result.data.url;
        link.setAttribute("download", "");
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        return {
          success: true,
          data: {
            payrollCount: result.data.payrollCount,
            bidCount: result.data.bidCount,
            consultantCount: result.data.consultantCount,
            includedCount: result.data.includedCount,
            excludedCount: result.data.excludedCount,
          },
        };
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    confirmPayroll: async (payrollId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/confirm/${payrollId}`,
          getHeader("POST", {})
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    settlePayroll: async (payrollId, { payDate, remark }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/settle/${payrollId}`,
          getHeader("POST", { payDate, remark })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },

    getHoldingBids: async (payrollId, params = {}) => {
      try {
        const qs = new URLSearchParams(params);
        const query = qs.toString();
        const url = `${process.env.REACT_APP_SERVER_URL}/payrollBid/getHoldingBids/${payrollId}${query ? `?${query}` : ""}`;
        let result = await fetch(url, getHeader("GET"));
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },

    getHoldingBidsByWallet: async (payrollId, walletId) => {
      try {
        const url = `${process.env.REACT_APP_SERVER_URL}/payrollBid/getHoldingBidsByWallet/${payrollId}?walletId=${walletId}`;
        let result = await fetch(url, getHeader("GET"));
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },

    getHoldingBidsByOrder: async (payrollId, groupKey) => {
      try {
        const url = `${process.env.REACT_APP_SERVER_URL}/payrollBid/getHoldingBidsByOrder/${payrollId}?groupKey=${groupKey}`;
        let result = await fetch(url, getHeader("GET"));
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },

    getReleasedHoldingBids: async (payrollId) => {
      try {
        const url = `${process.env.REACT_APP_SERVER_URL}/payrollBid/getReleasedHoldingBids/${payrollId}`;
        let result = await fetch(url, getHeader("GET"));
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },

    getReleasedFromPayrollBids: async (payrollId) => {
      try {
        const url = `${process.env.REACT_APP_SERVER_URL}/payrollBid/getReleasedFromPayrollBids/${payrollId}`;
        let result = await fetch(url, getHeader("GET"));
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },

    resetPoolReleasedHoldBids: async (payrollId) => {
      try {
        const url = `${process.env.REACT_APP_SERVER_URL}/payrollBid/resetPoolReleasedHoldBids/${payrollId}`;
        let result = await fetch(url, getHeader("POST"));
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },

    releaseHoldingBids: async (payrollId, bidIds) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/releaseHoldingBids`,
          getHeader("POST", { payrollId, bidIds })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },

    holdBackHoldingBids: async (payrollId, bidIds) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/holdBackHoldingBids`,
          getHeader("POST", { payrollId, bidIds })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },

    cancelHoldingBids: async (payrollId, bidIds) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/cancelHoldingBids`,
          getHeader("POST", { payrollId, bidIds })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },

    revertCancelledBids: async (bidIds) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payrollBid/revertCancelledBids`,
          getHeader("POST", { bidIds })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
  };

  const Payroll = {
    removeBids: async (id, bidIds) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payroll/removeBids`,
          getHeader("POST", { bidIds, id })
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    removePayment: async (id, paymentId) => {
      try {
        console.log("removePayment", id, paymentId);
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payroll/removePayment`,
          getHeader("POST", { paymentId, id })
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    getPendingPayroll: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payroll/getPendingPayroll/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        // console.log("getPending", result);
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    clearAll: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payroll/clearAll/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    getUpdate: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payroll/getUpdate/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    export: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payroll/export/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        console.log("resultx", result);
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    //Report By Consultant
    exportDetail: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payroll/exportDetail/${id}`,
          getHeader("GET")
        );
        // console.log(result);
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    //Report By Order
    exportDetailByOrder: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payroll/exportDetailByOrder/${id}`,
          getHeader("GET")
        );
        // console.log(result);
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    checkAllowanceAutoFullfill: async () => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payroll/checkAllowanceAutoFullfill`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    getPaymentDetail: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/payroll/getPaymentDetail/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const Product = {
    getReport: async ({ effectiveDate, percent = 100 }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/product/getReport`,
          getHeader("POST", {
            effectiveDate,
            percent,
          })
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
    getCommissionScheduleProductList: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/product/getCommissionScheduleProductList/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    getCustomCommissionConditions: async (
      id,
      { signedDate, professionalInvestor = null } = {}
    ) => {
      try {
        const params = new URLSearchParams();
        if (signedDate) {
          params.set(
            "signedDate",
            signedDate instanceof Date
              ? signedDate.toISOString()
              : String(signedDate)
          );
        }
        if (professionalInvestor !== null && professionalInvestor !== undefined) {
          params.set("professionalInvestor", String(professionalInvestor));
        }
        const query = params.toString();
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/product/getCustomCommissionConditions/${id}${query ? `?${query}` : ""}`,
          getHeader("GET")
        );
        const json = await result.json();
        return json.data ?? { fields: [] };
      } catch (error) {
        console.log(error);
        return { fields: [] };
      }
    },

    getCurrentCommissionRateTable: async ({ id, date }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/product/getCurrentCommissionRateTable`,
          getHeader("POST", {
            date,
            id,
          })
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        throw error;
      }
    },
  };

  const Allowance = {
    getStatSummary: async () => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/allowance/getStatSummary`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    terminate: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/allowance/terminate/${id}`,
          getHeader("GET")
        );
        result = await result.json();

        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    reactive: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/allowance/reactive/${id}`,
          getHeader("GET")
        );
        result = await result.json();

        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const ActivityLog = {
    getReport: async ({ fromDate, toDate }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/activityLog/getReport`,
          getHeader("POST", { fromDate, toDate })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
  };

  const getActivityLog = async (entityId, commentOnly = false, page = 1, pageSize = 20) => {
    try {
      if (!entityId) return null;
      const apiEntity = "activityLog";
      const searchQuery = activityLogSearchQuery(entityId, commentOnly);
      const result = await search({
        apiEntity,
        pageSize,
        currentPage: page,
        searchQuery,
        byAggregation: false,
        sort: { by: "createdAt", order: "desc" },
      });
      // Normalise: result may be an object {docs, total} or a plain array
      if (!result) return { docs: [], total: 0 };
      if (Array.isArray(result)) return { docs: result, total: result.length };
      return result;
    } catch (error) {
      return { docs: [], total: 0 };
    }
  };
  const addActivityLog = async ({ entityId, title, by, description, changes }) => {
    try {
      if (!entityId) return null;
      const values = {
        entityId,
        title,
        by,
        ...(description ? { description } : {}),
        ...(changes ? { changes } : {}),
      };
      let result = await add({ apiEntity: "activityLog", values });
      return result;
    } catch (error) {
      return null;
    }
  };

  const addCommentLog = async ({ entityId, title, by }) => {
    try {
      if (!entityId) return null;
      const values = {
        entityId,
        type: "COMMENT",
        title,
        by,
      };
      let result = await add({ apiEntity: "activityLog", values });
      return result;
    } catch (error) {
      return null;
    }
  };

  const CommissionSetting = {};

  const CommissionServer = {
    checkReleasingBids: async (orderId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionServer/checkReleasingBids/${orderId}`,
          getHeader("GET")
        );
        result = await result.json();
        return (
          result.data.isReleasing === "true" || result.data.isReleasing === true
        );
      } catch (error) {
        console.log(error);
      }
    },
    getOrderCommissionSchedule: async (orderId) => {
      try {
        // console.log("matchCommissionRowForOrder", orderId);
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionServer/getOrderCommissionSchedule/${orderId}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    resetCommissionSettingOverride: async (orderId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionServer/resetCommissionSettingOverride/${orderId}`,
          getHeader("GET")
        );
        result = await result.json();
        if (!result.success) {
          throw result.message;
        }
        return result.data;
      } catch (error) {
        showNotification({
          title: "Error",
          message: error,
          color: "red",
        });
      }
    },
    updateCommissionSettingOverride: async (orderId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionServer/updateCommissionSettingOverride/${orderId}`,
          getHeader("GET")
        );
        result = await result.json();
        if (!result.success) {
          throw result.message;
        }
        return result.data;
      } catch (error) {
        showNotification({
          title: "Error",
          message: error,
          color: "red",
        });
      }
    },

    customizeCommissionSettingForCreditNote: async (creditNoteId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionServer/customizeCommissionSettingForCreditNote/${creditNoteId}`,
          getHeader("GET")
        );
        result = await result.json();
        if (!result.success) {
          throw result.message;
        }
        return result.data;
      } catch (error) {
        showNotification({
          title: "Error",
          message: error,
          color: "red",
        });
      }
    },
    resetCommissionSettingOverrideForCreditNote: async (creditNoteId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/commissionServer/resetCommissionSettingOverrideForCreditNote/${creditNoteId}`,
          getHeader("GET")
        );
        result = await result.json();
        if (!result.success) {
          throw result.message;
        }
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const AIWorkFlow = {
    runWorkflow: async (workflowId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/aiWorkFlow/runWorkflow/${workflowId}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    getWorkflowCredentials: async (workflowId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/aiWorkFlow/getWorkflowCredentials/${workflowId}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    addCredential: async (values) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/aiWorkFlow/addCredential`,
          getHeader("POST", values)
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    getCredentials: async (workflowId) => {
      try {
        const result = await search({
          apiEntity: "workflowCredential",
          pageSize: 1000,
          searchQuery: { workflowId },
          currentPage: 1,
        });
        console.log("getCredentials", result);
        return result?.docs || [];
      } catch (error) {
        console.log(error);
      }
    },
    deleteCredential: async (credentialId) => {
      try {
        let result = await removeById({
          apiEntity: "workflowCredential",
          id: credentialId,
        });
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    updateCredential: async (credentialId, values) => {
      try {
        console.log("updateCredential", credentialId, values);
        let result = await updateById({
          apiEntity: "workflowCredential",
          id: credentialId,
          values,
        });
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const Provider = {
    getPortalReport: async () => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/provider/getPortalReport`,
          getHeader("GET")
        );

        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
    getPublicLogin: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/provider/getPublicLogin/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    getContactList: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/provider/getContactList/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    getCurrentCommissionScheduleProductList: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/provider/getCurrentCommissionScheduleProductList/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    importLoginBatch: async (file) => {
      try {
        const data = new FormData();
        data.append("file", file);
        data.append("filename", file.name);
        data.append("token", getAuthToken());

        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/provider/importLoginBatch`,
          {
            method: "POST",
            body: data,
            redirect: "follow",
          }
        );
        const json = await result.json();
        if (json.error) throw json.error;
        return json;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
    importAllLoginBatch: async (file, importData = {}) => {
      try {
        const data = new FormData();
        data.append("file", file);
        data.append("filename", file.name);
        data.append("token", getAuthToken());
        data.append("importData", JSON.stringify(importData));

        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/provider/importAllLoginBatch`,
          {
            method: "POST",
            body: data,
            redirect: "follow",
          }
        );
        const json = await result.json();
        if (json.error) throw json.error;
        return json;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
    importAllContactBatch: async (file, importData = {}) => {
      try {
        const data = new FormData();
        data.append("file", file);
        data.append("filename", file.name);
        data.append("token", getAuthToken());
        data.append("importData", JSON.stringify(importData));

        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/provider/importAllContactBatch`,
          {
            method: "POST",
            body: data,
            redirect: "follow",
          }
        );
        const json = await result.json();
        if (json.error) throw json.error;
        return json;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
  };

  const BankTransaction = {
    unMatch: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/bankTransaction/unMatch/${id}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
    linkCommissionStatement: async ({
      bankTransactionId,
      commissionStatementId,
      bankCharge,
    }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/bankTransaction/linkCommissionStatement`,
          getHeader("POST", {
            bankTransactionId,
            commissionStatementId,
            bankCharge,
          })
        );
        result = await result.json();
        if (!result.success) throw new Error(result.message || result.msg);
        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
    unlinkCommissionStatement: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/bankTransaction/unlinkCommissionStatement/${id}`,
          getHeader("POST", {})
        );
        result = await result.json();
        if (!result.success) throw new Error(result.message || result.msg);
        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
    getReport: async ({ fromDate, toDate, dateType }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/bankTransaction/getReport`,
          getHeader("POST", { fromDate, toDate, dateType })
        );

        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
  };

  const ApiKey = {
    create: async ({ name, description }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/apiKey`,
          getHeader("POST", { name, description })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    revoke: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/apiKey/revoke/${id}`,
          getHeader("POST", {})
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const CronJob = {
    listAvailable: async () => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/cronJob/listAvailable`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    create: async ({ name, schedule, description, active }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/cronJob`,
          getHeader("POST", { name, schedule, description, active })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    toggleActive: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/cronJob/toggleActive/${id}`,
          getHeader("POST", {})
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    runNow: async (id) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/cronJob/runNow/${id}`,
          getHeader("POST", {})
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const CronJobExecutionLog = {
    downloadResult: async (logId) => {
      await downloadCronExecutionResultCsv({
        serverUrl: process.env.REACT_APP_SERVER_URL,
        logId,
        token: getAuthToken(),
      });
      return { success: true };
    },
  };

  const ApprovalFundList = {
    getCurrentEffective: async () => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/approvalFundList/getCurrentEffective`,
          getHeader("GET")
        );
        const json = await result.json();
        if (!json?.success) return null;
        return json.data ?? { approvalFundList: null, funds: [] };
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    downloadTemplate: async (supplier) => {
      try {
        const params = supplier
          ? `?supplier=${encodeURIComponent(supplier)}`
          : "";
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/approvalFundList/downloadTemplate${params}`,
          getHeader("GET")
        );
        if (!result.ok) {
          throw new Error("Failed to download template");
        }
        const blob = await result.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        const contentDisposition = result.headers.get("Content-Disposition");
        let fileName = "fund-list-template.xlsx";
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=(.+)/);
          if (match) fileName = match[1];
        }
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        a.remove();
        return { success: true };
      } catch (error) {
        console.log(error);
        return { success: false, error: error.message };
      }
    },
    parseFundFile: async (file) => {
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("filename", file.name);
        formData.append("token", getAuthToken());
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/approvalFundList/parseFundFile`,
          {
            method: "POST",
            body: formData,
            redirect: "follow",
          }
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
        return { success: false, error: { msg: error.message } };
      }
    },
  };

  const Quiz = {
    getListStatistics: async () => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/listStatistics`,
          getHeader("GET")
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    publish: async (id) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/${id}/publish`,
          getHeader("POST")
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    close: async (id) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/${id}/close`,
          getHeader("POST")
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    reactivate: async (id) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/${id}/reactivate`,
          getHeader("POST")
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    getStatistics: async (id) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/${id}/statistics`,
          getHeader("GET")
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    addQuestion: async (quizId, question) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/${quizId}/questions`,
          getHeader("POST", question)
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    updateQuestion: async (questionId, question) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/questions/${questionId}`,
          getHeader("PUT", question)
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    deleteQuestion: async (questionId) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/questions/${questionId}`,
          getHeader("DELETE")
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    reorderQuestions: async (questionIds) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/questions/reorder`,
          getHeader("POST", { questionIds })
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    uploadDocument: async (quizId, file) => {
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("token", getAuthToken());
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/${quizId}/documents`,
          { method: "POST", body: formData, redirect: "follow" }
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    deleteDocument: async (documentId) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/documents/${documentId}`,
          getHeader("DELETE")
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    downloadDocument: async (documentId, fileName) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/documents/${documentId}/download`,
          getHeader("GET")
        );
        if (!result.ok) throw new Error("Download failed");
        const blob = await result.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName ?? "document";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } catch (error) {
        console.log(error);
      }
    },
    acknowledgeDocument: async (documentId) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/documents/${documentId}/acknowledge`,
          getHeader("POST")
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    getMyQuizzes: async () => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/my-quizzes`,
          getHeader("GET")
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    getPrep: async (id) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/${id}/prep`,
          getHeader("GET")
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    getTake: async (id) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/${id}/take`,
          getHeader("GET")
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    start: async (id) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/${id}/start`,
          getHeader("POST")
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    submit: async (id, answers) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/${id}/submit`,
          getHeader("POST", { answers })
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    retake: async (id) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/${id}/retake`,
          getHeader("POST")
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    getResult: async (id) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/${id}/result`,
          getHeader("GET")
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    getResults: async (id) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/${id}/results`,
          getHeader("GET")
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    exportResults: async (id, fileName) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/quiz/${id}/results/export`,
          getHeader("GET")
        );
        if (!result.ok) throw new Error("Export failed");
        const blob = await result.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName ?? "quiz-results.xlsx";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } catch (error) {
        console.log(error);
      }
    },
  };

  const Mca = {
    toggleStatus: async (id) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/mcaProcess/toggleStatus/${id}`,
        getHeader("POST")
      );
      const json = await result.json();
      if (!json?.success) return handleResponseError(json);
      return json;
    },
    submitRequest: async (body) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/mcaRequest/submit`,
        getHeader("POST", body)
      );
      const json = await result.json();
      if (!json?.success) return handleResponseError(json);
      return json;
    },
    submitEntity: async (body) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/mcaRequest/submitEntity`,
        getHeader("POST", body)
      );
      const json = await result.json();
      if (!json?.success) return handleResponseError(json);
      return json;
    },
    matchCreate: async (entityKey, entityId) => {
      const params = new URLSearchParams({
        entityKey,
        entityId: String(entityId),
      });
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/mcaRequest/matchCreate?${params}`,
        getHeader("GET")
      );
      const json = await result.json();
      if (!json?.success) return handleResponseError(json);
      return json;
    },
    resubmitRequest: async (id, body) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/mcaRequest/resubmit/${id}`,
        getHeader("POST", body)
      );
      const json = await result.json();
      if (!json?.success) return handleResponseError(json);
      return json;
    },
    approveStep: async (id, body) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/mcaRequest/approve/${id}`,
        getHeader("POST", body)
      );
      const json = await result.json();
      if (!json?.success) return handleResponseError(json);
      return json;
    },
    uploadStepFieldFile: async (id, fieldIndex, file) => {
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("fieldIndex", String(fieldIndex));
        formData.append("token", getAuthToken());
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/mcaRequest/uploadDocument/${id}`,
          { method: "POST", body: formData, redirect: "follow" }
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log(error);
      }
    },
    rejectStep: async (id, body) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/mcaRequest/reject/${id}`,
        getHeader("POST", body)
      );
      const json = await result.json();
      if (!json?.success) return handleResponseError(json);
      return json;
    },
    terminateRequest: async (id, body) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/mcaRequest/terminate/${id}`,
        getHeader("POST", body)
      );
      const json = await result.json();
      if (!json?.success) return handleResponseError(json);
      return json;
    },
    cancelRequest: async (id, body = {}) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/mcaRequest/cancel/${id}`,
        getHeader("POST", body)
      );
      const json = await result.json();
      if (!json?.success) return handleResponseError(json);
      return json;
    },
    getMyPending: async () => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/mcaRequest/myPending`,
        getHeader("GET")
      );
      const json = await result.json();
      if (!json?.success) return handleResponseError(json);
      return json;
    },
    getMySubmitted: async () => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/mcaRequest/mySubmitted`,
        getHeader("GET")
      );
      const json = await result.json();
      if (!json?.success) return handleResponseError(json);
      return json;
    },
    getStatSummary: async (preQuery) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/mcaRequest/getStatSummary`,
        getHeader("POST", { preQuery })
      );
      const json = await result.json();
      if (!json?.success) return handleResponseError(json);
      return json;
    },
    getTimeline: async (id) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/mcaRequest/timeline/${id}`,
        getHeader("GET")
      );
      const json = await result.json();
      if (!json?.success) return handleResponseError(json);
      return json;
    },
    getReportingEnabled: async () => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/mcaProcess/reportingEnabled`,
        getHeader("GET")
      );
      const json = await result.json();
      if (!json?.success) return handleResponseError(json);
      return json;
    },
    getReport: async ({ fromDate, toDate, dateType }) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/mcaRequest/getReport`,
          getHeader("POST", { fromDate, toDate, dateType })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const Notification = {
    toggleRuleStatus: async (id) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/notificationRule/toggleStatus/${id}`,
        getHeader("POST")
      );
      const json = await result.json();
      if (!json?.success) return handleResponseError(json);
      return json;
    },
    testRule: async (id) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/notificationRule/test/${id}`,
          getHeader("POST")
        );
        const json = await result.json();
        return json;
      } catch (error) {
        console.log("testRule failed:", error.message);
        return null;
      }
    },
    getVariables: async (entity) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/notificationRule/variables/${entity}`,
        getHeader("GET")
      );
      const json = await result.json();
      if (!json?.success) return handleResponseError(json);
      return json;
    },
    getLogStats: async () => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/notificationLog/stats`,
        getHeader("GET")
      );
      const json = await result.json();
      if (!json?.success) return handleResponseError(json);
      return json;
    },
    getLogsByEntity: async (entity, id) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/notificationLog/byEntity/${entity}/${id}`,
        getHeader("GET")
      );
      const json = await result.json();
      if (!json?.success) return handleResponseError(json);
      return json;
    },
    getInbox: async ({ page = 1, limit = 20, unreadOnly = false, category } = {}) => {
      try {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        if (unreadOnly) params.set("unreadOnly", "true");
        if (category) params.set("category", category);
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/userNotification/inbox?${params}`,
          getHeader("GET")
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log("Notification.getInbox failed:", error.message);
        return null;
      }
    },
    getUnreadCount: async () => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/userNotification/unreadCount`,
          getHeader("GET")
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log("Notification.getUnreadCount failed:", error.message);
        return null;
      }
    },
    markRead: async (id) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/userNotification/markRead/${id}`,
          getHeader("POST")
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log("Notification.markRead failed:", error.message);
        return null;
      }
    },
    markUnread: async (id) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/userNotification/markUnread/${id}`,
          getHeader("POST")
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log("Notification.markUnread failed:", error.message);
        return null;
      }
    },
    markAllRead: async () => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/userNotification/markAllRead`,
          getHeader("POST")
        );
        const json = await result.json();
        if (!json?.success) return handleResponseError(json);
        return json;
      } catch (error) {
        console.log("Notification.markAllRead failed:", error.message);
        return null;
      }
    },
  };

  const FileGroup = {
    getFolderTree: async (rootId = null) => {
      try {
        let url = `${process.env.REACT_APP_SERVER_URL}/fileGroup/getFolderTree`;
        if (rootId) {
          url += `?rootId=${rootId}`;
        }
        let result = await fetch(url, getHeader("GET"));
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    getAccessibleFolders: async () => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileGroup/getAccessibleFolders`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    getRootFolders: async () => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileGroup/getRootFolders`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    getSubFolders: async (parentId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileGroup/getSubFolders/${parentId}`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    uploadFile: async (groupId, file) => {
      try {
        let data = new FormData();
        data.append("file", file);
        data.append("filename", file.name);
        data.append("token", getAuthToken());

        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileGroup/uploadFile/${groupId}`,
          {
            method: "POST",
            body: data,
            redirect: "follow",
          }
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    removeFile: async (groupId, fileName) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileGroup/${groupId}/removeFile/${fileName}`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    share: async (groupId, targetUserId, permission) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileGroup/${groupId}/share`,
          getHeader("POST", { targetUserId, permission })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    removeShare: async (groupId, targetUserId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileGroup/${groupId}/removeShare/${targetUserId}`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    downloadFile: async (groupId, fileName) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileGroup/${groupId}/downloadFile/${fileName}`,
          getHeader("GET")
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    renameFile: async (groupId, fileName, newName) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileGroup/renameFile/${groupId}/${fileName}`,
          getHeader("POST", { newName })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    moveFile: async (sourceGroupId, fileName, targetGroupId) => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileGroup/${sourceGroupId}/moveFile/${fileName}`,
          getHeader("POST", { targetGroupId })
        );
        result = await result.json();
        return result;
      } catch (error) {
        console.log(error);
      }
    },

    getStats: async () => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileGroup/getStats`,
          getHeader("GET")
        );
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },

    search: async (query, type, parent, sort) => {
      try {
        let url = `${
          process.env.REACT_APP_SERVER_URL
        }/fileGroup/search?query=${encodeURIComponent(query)}`;
        if (type) url += `&type=${type}`;
        if (parent) url += `&parent=${parent}`;
        if (sort) url += `&sort=${sort}`;

        let result = await fetch(url, getHeader("GET"));
        result = await result.json();
        return result.data;
      } catch (error) {
        console.log(error);
      }
    },
  };

  const fileBackup = {
    getStatus: async () => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileBackup/status`,
          getHeader("GET")
        );
        return await result.json();
      } catch (error) {
        console.log(error);
      }
    },
    testConnection: async () => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileBackup/testConnection`,
          getHeader("GET")
        );
        return await result.json();
      } catch (error) {
        console.log(error);
      }
    },
    backfill: async () => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileBackup/backfill`,
          getHeader("POST", {})
        );
        return await result.json();
      } catch (error) {
        console.log(error);
      }
    },
    backfillStatus: async (runId) => {
      try {
        const params = runId ? `?runId=${encodeURIComponent(runId)}` : "";
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileBackup/backfillStatus${params}`,
          getHeader("GET")
        );
        return await result.json();
      } catch (error) {
        console.log(error);
      }
    },
    stopBackfill: async (runId) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileBackup/stopBackfill`,
          getHeader("POST", runId ? { runId } : {})
        );
        return await result.json();
      } catch (error) {
        console.log(error);
      }
    },
    processJobsNow: async () => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileBackup/processJobsNow`,
          getHeader("POST", {})
        );
        return await result.json();
      } catch (error) {
        console.log(error);
      }
    },
    reconcileDrive: async (batchSize) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileBackup/reconcileDrive`,
          getHeader("POST", batchSize ? { batchSize } : {})
        );
        return await result.json();
      } catch (error) {
        console.log(error);
      }
    },
    clearPending: async () => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileBackup/clearPending`,
          getHeader("POST", {})
        );
        return await result.json();
      } catch (error) {
        console.log(error);
      }
    },
    retryFailed: async () => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileBackup/retryFailed`,
          getHeader("POST", {})
        );
        return await result.json();
      } catch (error) {
        console.log(error);
      }
    },
    requeueToCurrentFolder: async () => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileBackup/requeueToCurrentFolder`,
          getHeader("POST", {})
        );
        return await result.json();
      } catch (error) {
        console.log(error);
      }
    },
    getJobs: async ({
      page = 1,
      pageSize = 20,
      status,
      action,
      entityType,
      entityCode,
      search,
    } = {}) => {
      try {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("pageSize", String(pageSize));
        if (status) params.set("status", status);
        if (action) params.set("action", action);
        if (entityType) params.set("entityType", entityType);
        if (entityCode) params.set("entityCode", entityCode);
        if (search) params.set("search", search);

        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileBackup/jobs?${params.toString()}`,
          getHeader("GET")
        );
        return await result.json();
      } catch (error) {
        console.log(error);
      }
    },
    saveSettings: async (payload) => {
      try {
        const result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/fileBackup/settings`,
          getHeader("POST", payload)
        );
        return await result.json();
      } catch (error) {
        console.log(error);
      }
    },
  };

  const documentSetting = {
    get: async () => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/documentSetting`,
        getHeader("GET")
      );
      return await result.json();
    },
    save: async (payload) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/documentSetting`,
        getHeader("PUT", payload)
      );
      return await result.json();
    },
    uploadTemplate: async (type, file) => {
      const data = new FormData();
      data.append("file", file);
      data.append("filename", file.name);
      data.append("token", getAuthToken());
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/documentSetting/uploadTemplate/${type}`,
        {
          method: "POST",
          body: data,
          headers: buildCompanyHeaders(
            { "x-access-token": getAuthToken() },
            getStoredCompanyId()
          ),
          redirect: "follow",
        }
      );
      return await result.json();
    },
    preview: async (type, format = "pdf") => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/documentSetting/preview/${type}?format=${format}`,
        getHeader("POST", { format })
      );
      return await result.json();
    },
    placeholders: async (type) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/documentSetting/placeholders/${type}`,
        getHeader("GET")
      );
      return await result.json();
    },
    downloadTemplate: async (type) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/documentSetting/template/${type}`,
        getHeader("GET")
      );
      if (!result.ok) {
        const json = await result.json().catch(() => null);
        throw new Error(json?.message || json?.error || "Download failed");
      }
      const blob = await result.blob();
      const disposition = result.headers.get("content-disposition") || "";
      const match = disposition.match(/filename="?([^"]+)"?/i);
      return { blob, filename: match?.[1] || `${type}.docx` };
    },
  };

  const Accounting = {
    initialize: async () => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/accountingReport/initialize`,
        getHeader("POST", {})
      );
      return (await result.json()).data;
    },
    seedChartOfAccounts: async () => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/chartOfAccount/seedTemplate`,
        getHeader("POST", {})
      );
      return (await result.json()).data;
    },
    seedPostingRules: async () => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/accountingPostingRule/seedDefaults`,
        getHeader("POST", {})
      );
      return (await result.json()).data;
    },
    getChartOfAccountsTree: async () => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/chartOfAccount/getTree`,
        getHeader("GET")
      );
      return (await result.json()).data;
    },
    getCurrentFiscalPeriod: async () => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/fiscalPeriod/getCurrent`,
        getHeader("GET")
      );
      return (await result.json()).data;
    },
    createYearWithPeriods: async (year, name) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/fiscalPeriod/createYearWithPeriods`,
        getHeader("POST", { year, name })
      );
      return (await result.json()).data;
    },
    closeFiscalPeriod: async (id) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/fiscalPeriod/close/${id}`,
        getHeader("POST", {})
      );
      return (await result.json()).data;
    },
    reopenFiscalPeriod: async (id) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/fiscalPeriod/reopen/${id}`,
        getHeader("POST", {})
      );
      return (await result.json()).data;
    },
    postManualJournal: async (payload) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/journalEntry/postManual`,
        getHeader("POST", payload)
      );
      const json = await result.json();
      if (!json?.success) {
        throw new Error(json?.error || json?.message || "Unable to post journal");
      }
      return json.data;
    },
    reverseJournal: async (id, reason) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/journalEntry/reverse/${id}`,
        getHeader("POST", { reason })
      );
      const json = await result.json();
      if (!json?.success) {
        throw new Error(json?.error || json?.message || "Unable to reverse journal");
      }
      return json.data;
    },
    getJournalsBySource: async (sourceType, sourceId) => {
      const params = new URLSearchParams({ sourceType, sourceId });
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/journalEntry/getBySource?${params}`,
        getHeader("GET")
      );
      return (await result.json()).data;
    },
    getTrialBalance: async (query = {}) => {
      const params = new URLSearchParams(query);
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/accountingReport/getTrialBalance?${params}`,
        getHeader("GET")
      );
      return (await result.json()).data;
    },
    getProfitAndLoss: async (query = {}) => {
      const params = new URLSearchParams(query);
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/accountingReport/getProfitAndLoss?${params}`,
        getHeader("GET")
      );
      return (await result.json()).data;
    },
    getBalanceSheet: async (query = {}) => {
      const params = new URLSearchParams(query);
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/accountingReport/getBalanceSheet?${params}`,
        getHeader("GET")
      );
      return (await result.json()).data;
    },
    getSubledgerReconciliation: async (query = {}) => {
      const params = new URLSearchParams(query);
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/accountingReport/getSubledgerReconciliation?${params}`,
        getHeader("GET")
      );
      return (await result.json()).data;
    },
    getAging: async (type, asOfDate) => {
      const params = new URLSearchParams({ type, asOfDate });
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/accountingReport/getAging?${params}`,
        getHeader("GET")
      );
      return (await result.json()).data;
    },
    getCommissionAnalysis: async (query = {}) => {
      const params = new URLSearchParams(query);
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/accountingReport/getCommissionAnalysis?${params}`,
        getHeader("GET")
      );
      return (await result.json()).data;
    },
    getGeneralLedger: async (query = {}) => {
      const params = new URLSearchParams(query);
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/accountingReport/getGeneralLedger?${params}`,
        getHeader("GET")
      );
      return (await result.json()).data;
    },
    postOpeningBalance: async (payload) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/accountingReport/postOpeningBalance`,
        getHeader("POST", payload)
      );
      return (await result.json()).data;
    },
    postProfitsTaxProvision: async (payload) => {
      const result = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/accountingReport/postProfitsTaxProvision`,
        getHeader("POST", payload)
      );
      return (await result.json()).data;
    },
  };

  const Company = {
    stampDefaultCompany: async () => {
      try {
        let result = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/company/stampDefaultCompany`,
          getHeader("POST", {})
        );
        result = await result.json();
        if (!result || !result.success) return handleResponseError(result);
        return result.data;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
  };

  // return [searchApi, addApi, deleteApi ]
  const api = {
    AIRequest,
    Client,
    ClientRelationship,
    Order,
    OrderService,
    OrderInvestmentTransaction,
    User,
    CommissionStatement,
    CommissionPayment,
    Payroll,
    Payroll2,
    PayrollPayment,
    Allowance,
    Product,
    Provider,
    Wallet,
    Bid,
    ClientPayment,
    Ticket,
    DataFeed,
    DataMigration,
    OrderProfolio,
    Company,
    CalendarEvent,
    ConsultantIncentive,
    Incentive,
    ClientPromotion,
    TopRanking,
    Quotation,
    DebitNote,
    CreditNote,
    Cpd,
    QuoRequest,
    Renewal,
    GIMatchPayment,
    GISettlement,
    BankAccount,
    BankTransaction,
    CommissionSchedule,
    CommissionServer,
    CommissionSetting,
    AIWorkFlow,
    ApiKey,
    ApprovalFundList,
    Quiz,
    Mca,
    Notification,
    CronJob,
    CronJobExecutionLog,
    ActivityLog,
    FileGroup,
    fileBackup,
    documentSetting,
    Accounting,
    search,
    aggregate,
    add,
    copyById,
    getDistinctValues,
    removeById,
    getById,
    updateById,
    getErrorMsg,
    uploadFile,
    downloadFiles,
    downloadZipFiles,
    importBatch,
    getExtraFieldSettingByEntityType,
    upsertExtraFieldSettingByEntityType,
    previewPointType,
    previewPromotionTrack,
    getPromotionProgress,

    getActivityLog,
    addCommentLog,
    getOverridingResult,
    addActivityLog,
    renameFile,
  };
  return [api];
};
